import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, UserPlus, Search,
  FileText, DollarSign, Calendar, CheckCircle, XCircle, Clock,
  Eye, Trash2, Plus, RefreshCw, Edit2, Printer,
  CreditCard, ClipboardList, GraduationCap, Home, Phone, Mail,
  Percent, X, ChevronLeft, AlertTriangle, RotateCcw
} from 'lucide-react';
import { Gate } from './AccessStudio';
import RegistrationPledgePrintModal from './modals/RegistrationPledgePrintModal';
import PledgeDiscountsEditor from './modals/PledgeDiscountsEditor';
import FamilyReportModal from './modals/FamilyReportModal';
import RegistrationReports from './RegistrationReports';
import RegistrationLookupsModal from './modals/RegistrationLookupsModal';

const Registration = ({ apiCall, selectedYear, hasRole, hasPermission, showSuccess, showError }) => {
  // ============================================
  // STATE
  // ============================================
  const [activeTab, setActiveTab] = useState('list');
  const [registrations, setRegistrations] = useState([]);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [registrationDetails, setRegistrationDetails] = useState(null);
  const [eligibleFamilies, setEligibleFamilies] = useState([]);
  const [classLevels, setClassLevels] = useState([]);
  const [feeTypes, setFeeTypes] = useState([]);
  // Payment-plan scheduler (date range + all-USD) replaces the old templates
  const [scheduleForm, setScheduleForm] = useState({ startDate: '', endDate: '', allUSD: false });
  const [tuitionRemaining, setTuitionRemaining] = useState(null);
  const [usdRate, setUsdRate] = useState(89500);
  const [usdRateInput, setUsdRateInput] = useState('');
  const [generatingPlan, setGeneratingPlan] = useState(false);

  // Overdue bills report (per-family aggregate)
  const [overdueData, setOverdueData] = useState(null);
  const [reconciling, setReconciling] = useState(false);

  // Tuition pricing admin (global fees + per-class overrides)
  const [pricingData, setPricingData] = useState(null);
  const [classPricingForm, setClassPricingForm] = useState({
    className: '', feeTypeId: '', studentType: 'All', amount: '', notes: ''
  });
  const [globalFeeForm, setGlobalFeeForm] = useState({
    feeTypeId: '', studentType: 'New', amount: '', notes: ''
  });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  // Modal states
  const [showNewRegistration, setShowNewRegistration] = useState(false);
  const [showReRegistration, setShowReRegistration] = useState(false);
  // Re-registration step 2: family whose children are being picked, and the rows
  const [reRegFamily, setReRegFamily] = useState(null);
  const [reRegChildren, setReRegChildren] = useState([]);
  const [reRegSearch, setReRegSearch] = useState('');

  // Family master details (Families table) + edit modal
  const [familyDetails, setFamilyDetails] = useState(null);
  const [showFamilyEdit, setShowFamilyEdit] = useState(false);
  const [reportMode, setReportMode] = useState(null); // null | 'family' | 'all'
  const [familyForm, setFamilyForm] = useState({});
  // Cascading محافظة → قضاء → محلة picker for the family's Lebanese registry place.
  const [geo, setGeo] = useState({ governorates: [], cazas: [], localities: [] });
  const [geoSel, setGeoSel] = useState({ governorateId: '', cazaId: '' });
  const [lookups, setLookups] = useState({});

  // Detail view: 'family' (info, children, documents) or 'financial'
  // (fees, summary, payment plan, SIS payments)
  const [detailView, setDetailView] = useState('family');
  // Which children's fee groups are expanded in the fees breakdown
  const [expandedFeeChildren, setExpandedFeeChildren] = useState([]);
  // Possible existing families matching what's typed in the New Family modal
  const [dupMatches, setDupMatches] = useState([]);

  // Printable payment pledge (تعهد) — modal component owns its own form
  const [showPrintPlan, setShowPrintPlan] = useState(false);
  const [pledgeAllowEdit, setPledgeAllowEdit] = useState(true); // Print = edit; Reprint = reprint-only

  // Lookup lists admin (SystemLookups manager, GlobalAdmin only)
  const [showLookupsManager, setShowLookupsManager] = useState(false);
  const [sisData, setSisData] = useState(null);
  const [sisError, setSisError] = useState('');
  const [sisLoading, setSisLoading] = useState(false);
  const [sisNumberInput, setSisNumberInput] = useState('');
  const [showAddChild, setShowAddChild] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [showEditInstallment, setShowEditInstallment] = useState(false);
  const [showAddInstallment, setShowAddInstallment] = useState(false);

  // Form states
  const [newFamilyForm, setNewFamilyForm] = useState({
    fatherName: '', motherName: '', primaryPhone: '', email: '', address: ''
  });
  const [newChildForm, setNewChildForm] = useState({
    firstName: '', lastName: '', dateOfBirth: '', gender: '', applyingForClass: '', isNewStudent: true
  });
  // Add Child: choose an EXISTING student in the family vs. create a NEW one
  const [addChildMode, setAddChildMode] = useState('new'); // 'existing' | 'new'
  const [familyStudents, setFamilyStudents] = useState([]); // family students not already on this registration
  const [existingChildForm, setExistingChildForm] = useState({ studentId: '', applyingForClass: '' });
  // Change a child's class (re-prices fees + updates academic record)
  const [changeClassChild, setChangeClassChild] = useState(null); // the child object
  const [changeClassValue, setChangeClassValue] = useState('');
  const [discountForm, setDiscountForm] = useState({ discountUSD: 0, discountLBP: 0, discountReason: '' });
  const [installmentForm, setInstallmentForm] = useState({});
  const [addInstallmentForm, setAddInstallmentForm] = useState({
    installmentName: '', dueDate: '', amountUSD: 0, amountLBP: 0, notes: ''
  });

  // Target year for registration (next year)
  const registrationYear = '26-27';

  // ============================================
  // DATA LOADING
  // ============================================
  const loadRegistrations = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiCall(`/registration?academicYear=${registrationYear}&status=${statusFilter}`);
      setRegistrations(data);
    } catch (err) {
      showError('Failed to load registrations: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [apiCall, registrationYear, statusFilter, showError]);

  const loadRegistrationDetails = useCallback(async (id) => {
    try {
      setLoading(true);
      const data = await apiCall(`/registration/${id}`);
      setRegistrationDetails(data);
      setSelectedRegistration(data.registration);
      // Single child: open its fee group; several: start collapsed (compact)
      setExpandedFeeChildren(
        (data.children || []).length === 1 ? [data.children[0].ChildID] : []
      );
      // Family master data (religion, jobs, phones...) lives on Families
      if (data.registration?.FamilyID) {
        try {
          const fd = await apiCall(`/registration/family-details/${data.registration.FamilyID}`);
          setFamilyDetails(fd);
          setSisNumberInput(fd.SISNumber || fd.FamilyID || '');
        } catch (_) {
          setFamilyDetails(null); // new-family registrations have no Families row yet
          setSisNumberInput('');
        }
      } else {
        setFamilyDetails(null);
        setSisNumberInput('');
      }
      setSisData(null);
      setSisError('');
      // NOTE: detailView is intentionally NOT reset here — saving an
      // installment (etc.) reloads details and must keep the current view.
    } catch (err) {
      showError('Failed to load registration details: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [apiCall, showError]);

  const loadPricing = useCallback(async () => {
    try {
      setPricingData(await apiCall(`/registration/pricing/${registrationYear}`));
    } catch (err) {
      showError('Failed to load pricing: ' + err.message);
    }
  }, [apiCall, registrationYear, showError]);

  const loadOverdue = useCallback(async () => {
    try {
      setOverdueData(await apiCall(`/registration/overdue-report/${registrationYear}`));
    } catch (err) {
      showError('Failed to load overdue report: ' + err.message);
    }
  }, [apiCall, registrationYear, showError]);

  // Batch-apply SIS tuition payments to every current-year plan (one SIS round
  // trip), then refresh — so families who paid in SIS drop off the overdue list.
  const handleReconcileSis = useCallback(async () => {
    setReconciling(true);
    try {
      const r = await apiCall(`/registration/sis-apply-all/${registrationYear}`, { method: 'POST' });
      showSuccess(r?.message || 'Reconciled with SIS');
      await loadOverdue();
    } catch (err) {
      showError('SIS reconcile failed: ' + err.message);
    } finally {
      setReconciling(false);
    }
  }, [apiCall, registrationYear, showSuccess, showError, loadOverdue]);

  const loadLookups = useCallback(async () => {
    try {
      setLookups(await apiCall('/registration/lookups'));
    } catch (err) {
      console.error('Failed to load lookups:', err);
    }
  }, [apiCall]);

  const loadEligibleFamilies = useCallback(async () => {
    try {
      const data = await apiCall(`/registration/eligible-families/${registrationYear}`);
      setEligibleFamilies(data);
    } catch (err) {
      showError('Failed to load eligible families: ' + err.message);
    }
  }, [apiCall, registrationYear, showError]);

  const loadClassLevels = useCallback(async () => {
    try {
      const data = await apiCall('/registration/class-levels');
      setClassLevels(data);
    } catch (err) {
      console.error('Failed to load class levels:', err);
    }
  }, [apiCall]);

  const loadFeeTypes = useCallback(async () => {
    try {
      const data = await apiCall('/registration/fee-types');
      setFeeTypes(data);
    } catch (err) {
      console.error('Failed to load fee types:', err);
    }
  }, [apiCall]);

  const loadUsdRate = useCallback(async () => {
    try {
      const data = await apiCall('/registration/settings/usd-rate');
      if (data?.rate > 0) { setUsdRate(data.rate); setUsdRateInput(String(data.rate)); }
    } catch (err) {
      console.error('Failed to load USD rate:', err);
    }
  }, [apiCall]);

  const handleSaveUsdRate = async () => {
    const rate = parseFloat(usdRateInput);
    if (!(rate > 0)) { showError('Rate must be a positive number'); return; }
    try {
      await apiCall('/registration/settings/usd-rate', {
        method: 'PUT',
        body: JSON.stringify({ rate })
      });
      setUsdRate(rate);
      showSuccess('USD → LBP rate updated');
    } catch (err) {
      showError('Failed to update rate: ' + err.message);
    }
  };

  const loadStats = useCallback(async () => {
    try {
      const data = await apiCall(`/registration/stats/${registrationYear}`);
      setStats(data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  }, [apiCall, registrationYear]);

  useEffect(() => {
    loadRegistrations();
    loadClassLevels();
    loadFeeTypes();
    loadUsdRate();
    loadStats();
    loadLookups();
  }, [loadRegistrations, loadClassLevels, loadFeeTypes, loadUsdRate, loadStats, loadLookups]);

  // Opening the Financial tab keeps the plan in sync with SIS: it silently
  // re-applies SIS payments (so Paid/Status stay current without clicking),
  // loads the SIS payment list + remaining balance, and seeds the scheduler
  // dates (Aug 5 → May 5 of the target year). Keyed on the tab + registration
  // only; the loaders/flags are read, not deps, to avoid re-firing every render.
  useEffect(() => {
    const regId = selectedRegistration?.RegistrationID;
    const familyId = selectedRegistration?.FamilyID;
    if (detailView !== 'financial' || !regId) return;
    const startYear = 2000 + parseInt(registrationYear.slice(0, 2), 10);
    setScheduleForm(f => (f.startDate && f.endDate) ? f : {
      startDate: `${startYear}-08-05`,
      endDate: `${startYear + 1}-05-05`,
      allUSD: false
    });
    (async () => {
      // Silent, idempotent: mark installments paid from SIS. No-ops off-network
      // or before the plan exists. Reload the plan only when something changed.
      let applied = false;
      if (familyId) {
        try {
          const r = await apiCall(`/registration/${regId}/sis-apply`, { method: 'POST' });
          applied = (r?.appliedToPlan?.USD || 0) > 0 || (r?.appliedToPlan?.LBP || 0) > 0;
        } catch (_) { /* SIS unreachable or plan not set up yet — ignore */ }
      }
      if (applied) await loadRegistrationDetails(regId);
      loadTuitionRemaining(regId);
      if (familyId) loadSisPayments();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailView, selectedRegistration?.RegistrationID]);

  // While typing a new family's parents, look for existing families with a
  // matching name so duplicates get caught before they're created.
  useEffect(() => {
    if (!showNewRegistration) { setDupMatches([]); return; }
    const q = (newFamilyForm.fatherName?.length >= 3 && newFamilyForm.fatherName)
      || (newFamilyForm.motherName?.length >= 3 && newFamilyForm.motherName) || '';
    if (!q) { setDupMatches([]); return; }
    const t = setTimeout(async () => {
      try {
        setDupMatches(await apiCall(`/registration/family-search?q=${encodeURIComponent(q)}`));
      } catch (_) { setDupMatches([]); }
    }, 400);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showNewRegistration, newFamilyForm.fatherName, newFamilyForm.motherName]);

  // ============================================
  // HANDLERS
  // ============================================
  // Step 1 → 2: load the family's children with suggested next classes
  const handleOpenReRegPreview = async (family) => {
    try {
      setLoading(true);
      const preview = await apiCall(`/registration/re-register/${family.FamilyID}/preview?academicYear=${registrationYear}`);
      setReRegChildren(preview.map(c => ({
        ...c,
        include: !c.IsGraduating,
        // Seconde students (SectionOptions set) must pick their section explicitly
        applyingForClass: c.SuggestedClass || (c.SectionOptions ? '' : c.CurrentClass) || ''
      })));
      setReRegFamily(family);
    } catch (err) {
      showError('Failed to load children: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReRegistration = async () => {
    const chosen = reRegChildren.filter(c => c.include);
    if (chosen.length === 0) {
      showError('Select at least one child to register');
      return;
    }
    if (chosen.some(c => !c.applyingForClass)) {
      showError('Every selected child needs a class');
      return;
    }
    try {
      setLoading(true);
      await apiCall(`/registration/re-register/${reRegFamily.FamilyID}`, {
        method: 'POST',
        body: JSON.stringify({
          academicYear: registrationYear,
          children: chosen.map(c => ({ studentId: c.StudentID, applyingForClass: c.applyingForClass }))
        })
      });
      showSuccess('Re-registration created successfully');
      setReRegFamily(null);
      setReRegChildren([]);
      setShowReRegistration(false);
      loadRegistrations();
      loadEligibleFamilies();
      loadStats();
    } catch (err) {
      showError('Failed to create re-registration: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadSisPayments = async () => {
    if (!selectedRegistration?.FamilyID) return;
    try {
      setSisLoading(true);
      setSisError('');
      const data = await apiCall(
        `/registration/sis-payments/${selectedRegistration.FamilyID}?academicYear=${registrationYear}`
      );
      setSisData(data);
    } catch (err) {
      setSisData(null);
      setSisError(err.message);
    } finally {
      setSisLoading(false);
    }
  };

  // Apply SIS payments to the plan: deducts registration/services first,
  // then fills installments oldest-first and marks covered ones Paid
  const handleSisApply = async () => {
    if (!selectedRegistration?.RegistrationID) return;
    try {
      setSisLoading(true);
      const r = await apiCall(`/registration/${selectedRegistration.RegistrationID}/sis-apply`, { method: 'POST' });
      showSuccess(r.message);
      loadRegistrationDetails(selectedRegistration.RegistrationID);
    } catch (err) {
      showError(err.message);
    } finally {
      setSisLoading(false);
    }
  };

  // Saving the SIS number goes through the full family-details PUT (partial
  // updates would wipe the other fields), so rebuild the payload from state.
  const handleSaveSisNumber = async () => {
    if (!familyDetails) return;
    try {
      setSisLoading(true);
      await apiCall(`/registration/family-details/${familyDetails.FamilyID}`, {
        method: 'PUT',
        body: JSON.stringify({
          fatherName: familyDetails.FatherName, motherName: familyDetails.MotherName,
          fatherPhone: familyDetails.FatherPhone, motherPhone: familyDetails.MotherPhone,
          email: familyDetails.Email, address: familyDetails.Address,
          religion: familyDetails.Religion, maritalStatus: familyDetails.MaritalStatus,
          fatherJob: familyDetails.FatherJob, motherJob: familyDetails.MotherJob,
          fatherStatus: familyDetails.FatherStatus, motherStatus: familyDetails.MotherStatus,
          fatherJobSector: familyDetails.FatherJobSector, motherJobSector: familyDetails.MotherJobSector,
          fatherIsAlumni: familyDetails.FatherIsAlumni, motherIsAlumni: familyDetails.MotherIsAlumni,
          sisNumber: sisNumberInput.trim() || null
        })
      });
      setFamilyDetails({ ...familyDetails, SISNumber: sisNumberInput.trim() || null });
      showSuccess('SIS number saved');
    } catch (err) {
      showError('Failed to save SIS number: ' + err.message);
    } finally {
      setSisLoading(false);
    }
  };

  // ---- Tuition pricing admin ----
  const setGlobalFeeField = (idx, field, value) => setPricingData(pd => ({
    ...pd, globalFees: pd.globalFees.map((f, i) => i === idx ? { ...f, [field]: value } : f)
  }));
  const setClassPricingField = (idx, field, value) => setPricingData(pd => ({
    ...pd, classPricing: pd.classPricing.map((r, i) => i === idx ? { ...r, [field]: value } : r)
  }));

  const handleSaveGlobalFee = async (fee) => {
    try {
      await apiCall(`/registration/pricing/global/${fee.GlobalFeeID}`, {
        method: 'PUT',
        body: JSON.stringify({
          amount: parseFloat(fee.DefaultAmount) || 0,
          notes: fee.Notes || null,
          studentType: fee.StudentType || 'All'
        })
      });
      showSuccess(`${fee.FeeTypeName} updated`);
      loadPricing();
    } catch (err) {
      showError('Failed to update fee: ' + err.message);
    }
  };

  const handleAddGlobalFee = async () => {
    if (!globalFeeForm.feeTypeId || globalFeeForm.amount === '') {
      showError('Fee type and amount are required');
      return;
    }
    try {
      await apiCall('/registration/pricing/global', {
        method: 'POST',
        body: JSON.stringify({
          academicYear: registrationYear,
          feeTypeId: parseInt(globalFeeForm.feeTypeId),
          amount: parseFloat(globalFeeForm.amount),
          studentType: globalFeeForm.studentType,
          notes: globalFeeForm.notes || null
        })
      });
      showSuccess('Global fee added');
      setGlobalFeeForm({ feeTypeId: '', studentType: 'New', amount: '', notes: '' });
      loadPricing();
    } catch (err) {
      showError('Failed to add global fee: ' + err.message);
    }
  };

  const handleDeleteGlobalFee = async (fee) => {
    if (!window.confirm(`Remove the ${fee.StudentType} global fee for ${fee.FeeTypeName}?`)) return;
    try {
      await apiCall(`/registration/pricing/global/${fee.GlobalFeeID}`, { method: 'DELETE' });
      showSuccess('Global fee removed');
      loadPricing();
    } catch (err) {
      showError('Failed to remove global fee: ' + err.message);
    }
  };

  const handleSyncFees = async () => {
    if (!window.confirm(
      `Apply current ${registrationYear} pricing to all existing registrations?\n\n` +
      'Fees that are missing for a child (priced after the family registered) are added. ' +
      'Existing fees, discounts, and payments are never touched.'
    )) return;
    try {
      setLoading(true);
      const result = await apiCall('/registration/sync-fees', {
        method: 'POST',
        body: JSON.stringify({ academicYear: registrationYear })
      });
      showSuccess(result.message);
      loadStats();
    } catch (err) {
      showError('Failed to sync fees: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddClassPricing = async () => {
    const { className, feeTypeId, amount } = classPricingForm;
    if (!className || !feeTypeId || !amount) {
      showError('Class, fee type, and amount are required');
      return;
    }
    try {
      await apiCall('/registration/pricing/class', {
        method: 'POST',
        body: JSON.stringify({
          academicYear: registrationYear,
          className,
          feeTypeId: parseInt(feeTypeId),
          amount: parseFloat(amount),
          studentType: classPricingForm.studentType,
          notes: classPricingForm.notes || null
        })
      });
      showSuccess('Class pricing added');
      setClassPricingForm({ className: '', feeTypeId: '', studentType: 'All', amount: '', notes: '' });
      loadPricing();
    } catch (err) {
      showError('Failed to add class pricing: ' + err.message);
    }
  };

  const handleSaveClassPricing = async (row) => {
    try {
      await apiCall(`/registration/pricing/class/${row.PricingID}`, {
        method: 'PUT',
        body: JSON.stringify({
          amount: parseFloat(row.Amount) || 0,
          studentType: row.StudentType || 'All',
          notes: row.Notes || null
        })
      });
      showSuccess(`${row.ClassName} — ${row.FeeTypeName} updated`);
      loadPricing();
    } catch (err) {
      showError('Failed to update class pricing: ' + err.message);
    }
  };

  const handleDeleteClassPricing = async (row) => {
    if (!window.confirm(`Remove ${row.FeeTypeName} pricing for ${row.ClassName}?`)) return;
    try {
      await apiCall(`/registration/pricing/class/${row.PricingID}`, { method: 'DELETE' });
      showSuccess('Class pricing removed');
      loadPricing();
    } catch (err) {
      showError('Failed to remove class pricing: ' + err.message);
    }
  };

  // --- Cascading geography picker (محافظة → قضاء → محلة) ---
  const loadCazas = async (govId) => {
    if (!govId) return [];
    try { return (await apiCall(`/registration/geo/cazas/${govId}`)) || []; } catch (_) { return []; }
  };
  const loadLocalities = async (cazaId) => {
    if (!cazaId) return [];
    try { return (await apiCall(`/registration/geo/localities/${cazaId}`)) || []; } catch (_) { return []; }
  };
  // Seed the picker to a family's saved place (governorate/caza/locality).
  const initGeoForFamily = async (govId, cazaId, localityId) => {
    setGeoSel({ governorateId: govId || '', cazaId: cazaId || '' });
    let governorates = [];
    try { governorates = (await apiCall('/registration/geo/governorates')) || []; } catch (_) { governorates = []; }
    const [cazas, localities] = await Promise.all([loadCazas(govId), loadLocalities(cazaId)]);
    setGeo({ governorates, cazas, localities });
  };
  const onGeoGovernorate = async (govId) => {
    setGeoSel({ governorateId: govId, cazaId: '' });
    setFamilyForm(f => ({ ...f, registrationPlaceId: '' }));
    setGeo(g => ({ ...g, cazas: [], localities: [] }));
    const cazas = await loadCazas(govId);
    setGeo(g => ({ ...g, cazas }));
  };
  const onGeoCaza = async (cazaId) => {
    setGeoSel(s => ({ ...s, cazaId }));
    setFamilyForm(f => ({ ...f, registrationPlaceId: '' }));
    setGeo(g => ({ ...g, localities: [] }));
    const localities = await loadLocalities(cazaId);
    setGeo(g => ({ ...g, localities }));
  };

  // One grouped parent card (Father / Mother) for the edit form: English name,
  // Arabic name beneath it, phone + dual nationality, old-student/deceased flags,
  // then a boxed Work group. Keys are `${role}${Suffix}` on familyForm.
  const renderParentCard = (role, title) => {
    const val = (s) => familyForm[`${role}${s}`] || '';
    const chk = (s) => !!familyForm[`${role}${s}`];
    const set = (s, v) => setFamilyForm(f => ({ ...f, [`${role}${s}`]: v }));
    const inp = 'w-full border rounded-lg px-2 py-1.5 text-sm';
    const lbl = 'block text-[11px] text-gray-500 mb-0.5';
    return (
      <div className="border rounded-lg p-3 space-y-2 bg-gray-50/40">
        <div className="text-sm font-semibold text-gray-700">{title}</div>
        <div>
          <label className={lbl}>Name (English)</label>
          <input type="text" value={val('Name')} onChange={e => set('Name', e.target.value)} className={inp} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className={lbl}>First (عربي)</label>
            <input dir="rtl" type="text" value={val('FirstNameAr')} onChange={e => set('FirstNameAr', e.target.value)} className={inp} />
          </div>
          <div>
            <label className={lbl}>Last (عربي)</label>
            <input dir="rtl" type="text" value={val('LastNameAr')} onChange={e => set('LastNameAr', e.target.value)} className={inp} />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className={lbl}>Phone</label>
            <input type="text" value={val('Phone')} onChange={e => set('Phone', e.target.value)} className={inp} />
          </div>
          <div>
            <label className={lbl}>Nationality 1</label>
            <input type="text" value={val('Nationality1')} onChange={e => set('Nationality1', e.target.value)} className={inp} />
          </div>
          <div>
            <label className={lbl}>Nationality 2</label>
            <input type="text" value={val('Nationality2')} onChange={e => set('Nationality2', e.target.value)} className={inp} />
          </div>
        </div>
        <div className="flex items-center gap-4 pt-0.5">
          <label className="flex items-center gap-1.5 text-sm">
            <input type="checkbox" checked={chk('IsAlumni')} onChange={e => set('IsAlumni', e.target.checked)} className="w-4 h-4" />
            Old student
          </label>
          <label className="flex items-center gap-1.5 text-sm">
            <input type="checkbox" checked={chk('Deceased')} onChange={e => set('Deceased', e.target.checked)} className="w-4 h-4" />
            Deceased
          </label>
        </div>
        <div className="border-t pt-2">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">Work — العمل</div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={lbl}>Job</label>
              <input type="text" value={val('Job')} onChange={e => set('Job', e.target.value)} className={inp} />
            </div>
            <div>
              <label className={lbl}>Status</label>
              <select value={val('Status')} onChange={e => set('Status', e.target.value)} className={inp}>
                <option value="">— not set —</option>
                {(lookups.EmploymentStatus || []).map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className={lbl}>Sector</label>
              <select value={val('JobSector')} onChange={e => set('JobSector', e.target.value)} className={inp}>
                <option value="">— not set —</option>
                {(lookups.JobSector || []).map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className={lbl}>Company</label>
              <input type="text" value={val('Company')} onChange={e => set('Company', e.target.value)} className={inp} />
            </div>
            <div className="col-span-2">
              <label className={lbl}>Work Address</label>
              <input type="text" value={val('WorkAddress')} onChange={e => set('WorkAddress', e.target.value)} className={inp} />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleSaveFamilyDetails = async () => {
    try {
      setLoading(true);
      await apiCall(`/registration/family-details/${familyDetails.FamilyID}`, {
        method: 'PUT',
        body: JSON.stringify(familyForm)
      });
      showSuccess('Family details updated');
      setShowFamilyEdit(false);
      setFamilyDetails(await apiCall(`/registration/family-details/${familyDetails.FamilyID}`));
    } catch (err) {
      showError('Failed to update family details: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNewRegistration = async () => {
    try {
      setLoading(true);
      const result = await apiCall('/registration', {
        method: 'POST',
        body: JSON.stringify({
          academicYear: registrationYear,
          registrationType: 'New',
          ...newFamilyForm
        })
      });
      showSuccess('Registration created: ' + result.RegistrationNumber);
      setShowNewRegistration(false);
      setNewFamilyForm({ fatherName: '', motherName: '', primaryPhone: '', email: '', address: '' });
      loadRegistrations();
      loadStats();
      // Open the new registration
      loadRegistrationDetails(result.RegistrationID);
      setActiveTab('detail');
    } catch (err) {
      showError('Failed to create registration: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Register a NEW student under an EXISTING family (e.g. a daycare-only family
  // with no school children — it won't appear in Re-Register, and creating a
  // "new family" would duplicate it). Reuses the existing FamilyID.
  const handleUseExistingFamily = async (m) => {
    try {
      setLoading(true);
      const result = await apiCall('/registration', {
        method: 'POST',
        body: JSON.stringify({
          academicYear: registrationYear,
          registrationType: 'New',
          familyId: m.FamilyID,
          fatherName: m.FatherName,
          motherName: m.MotherName,
          primaryPhone: m.PrimaryPhone
        })
      });
      showSuccess('Registration created for existing family: ' + result.RegistrationNumber);
      setShowNewRegistration(false);
      setNewFamilyForm({ fatherName: '', motherName: '', primaryPhone: '', email: '', address: '' });
      setDupMatches([]);
      loadRegistrations();
      loadStats();
      loadRegistrationDetails(result.RegistrationID);
      setActiveTab('detail');
    } catch (err) {
      showError('Failed to create registration: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkReRegistration = async () => {
    if (!window.confirm('This will create re-registrations for ALL eligible families. Continue?')) return;
    try {
      setLoading(true);
      const result = await apiCall('/registration/bulk-re-register', {
        method: 'POST',
        body: JSON.stringify({ academicYear: registrationYear })
      });
      showSuccess(`Created ${result.RegistrationsCreated} registrations`);
      loadRegistrations();
      loadEligibleFamilies();
      loadStats();
    } catch (err) {
      showError('Bulk registration failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Open the Add Child modal, loading the family's EXISTING students that
  // aren't already on this registration (e.g. a sibling the family skipped).
  const openAddChild = async () => {
    setNewChildForm({ firstName: '', lastName: '', dateOfBirth: '', gender: '', applyingForClass: '', isNewStudent: true });
    setExistingChildForm({ studentId: '', applyingForClass: '' });
    setFamilyStudents([]);
    setAddChildMode('new');
    setShowAddChild(true);
    try {
      const famId = selectedRegistration?.FamilyID;
      if (!famId) return;
      const students = await apiCall(`/students?familyId=${encodeURIComponent(famId)}`);
      const onReg = new Set((registrationDetails?.children || []).map(c => c.StudentID).filter(Boolean));
      const eligible = (Array.isArray(students) ? students : []).filter(s => s.StudentID && !onReg.has(s.StudentID));
      setFamilyStudents(eligible);
      if (eligible.length > 0) setAddChildMode('existing'); // default to the common case
    } catch (_) { setFamilyStudents([]); }
  };

  const handleAddChild = async () => {
    try {
      setLoading(true);
      const body = addChildMode === 'existing'
        ? { studentId: existingChildForm.studentId, applyingForClass: existingChildForm.applyingForClass, isNewStudent: false }
        : newChildForm;
      await apiCall(`/registration/${selectedRegistration.RegistrationID}/children`, {
        method: 'POST',
        body: JSON.stringify(body)
      });
      showSuccess('Child added successfully');
      setShowAddChild(false);
      setNewChildForm({ firstName: '', lastName: '', dateOfBirth: '', gender: '', applyingForClass: '', isNewStudent: true });
      setExistingChildForm({ studentId: '', applyingForClass: '' });
      loadRegistrationDetails(selectedRegistration.RegistrationID);
    } catch (err) {
      showError('Failed to add child: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveChild = async (childId) => {
    if (!window.confirm('Remove this child from the registration?')) return;
    try {
      const result = await apiCall(`/registration/${selectedRegistration.RegistrationID}/children/${childId}`, {
        method: 'DELETE'
      });
      if (result?.registrationDeleted) {
        showSuccess('Child removed — registration had no remaining children and was deleted');
        setSelectedRegistration(null);
        setRegistrationDetails(null);
        setActiveTab('list');
        loadRegistrations();
      } else {
        showSuccess('Child removed');
        loadRegistrationDetails(selectedRegistration.RegistrationID);
      }
    } catch (err) {
      showError('Failed to remove child: ' + err.message);
    }
  };

  const handleApplyDiscount = async () => {
    try {
      setLoading(true);
      await apiCall(`/registration/${selectedRegistration.RegistrationID}/discount`, {
        method: 'PUT',
        body: JSON.stringify(discountForm)
      });
      showSuccess('Discount applied');
      setShowDiscountModal(false);
      loadRegistrationDetails(selectedRegistration.RegistrationID);
    } catch (err) {
      showError('Failed to apply discount: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    try {
      await apiCall(`/registration/${selectedRegistration.RegistrationID}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus })
      });
      showSuccess(`Status updated to ${newStatus}`);
      loadRegistrationDetails(selectedRegistration.RegistrationID);
      loadRegistrations();
      loadStats();
    } catch (err) {
      showError('Failed to update status: ' + err.message);
    }
  };

  const handleEnroll = async () => {
    if (!window.confirm('Finalize enrollment? This will create student records for the new academic year.')) return;
    try {
      setLoading(true);
      const result = await apiCall(`/registration/${selectedRegistration.RegistrationID}/enroll`, {
        method: 'POST'
      });
      showSuccess(`Enrolled! Family: ${result.FamilyID}, Children: ${result.ChildrenEnrolled}`);
      loadRegistrationDetails(selectedRegistration.RegistrationID);
      loadRegistrations();
      loadStats();
    } catch (err) {
      showError('Enrollment failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const openChangeClass = (child) => {
    setChangeClassChild(child);
    setChangeClassValue(child.ApplyingForClass || '');
  };

  const handleChangeClass = async () => {
    if (!changeClassChild || !changeClassValue || changeClassValue === changeClassChild.ApplyingForClass) {
      setChangeClassChild(null);
      return;
    }
    try {
      setLoading(true);
      await apiCall(`/registration/${selectedRegistration.RegistrationID}/children/${changeClassChild.ChildID}/class`, {
        method: 'PUT',
        body: JSON.stringify({ newClass: changeClassValue })
      });
      showSuccess('Class changed — fees re-priced' + (changeClassChild.StudentID ? ' and academic record updated' : ''));
      setChangeClassChild(null);
      loadRegistrationDetails(selectedRegistration.RegistrationID);
    } catch (err) {
      showError('Failed to change class: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRevertEnrollment = async () => {
    if (!window.confirm(
      'Revert this enrollment?\n\nThe students\' academic records will be set to Withdrawn and the registration goes back to Accepted. Fees and payment plan are kept. You can press Enroll again to restore.'
    )) return;
    try {
      setLoading(true);
      const result = await apiCall(`/registration/${selectedRegistration.RegistrationID}/revert-enrollment`, {
        method: 'POST'
      });
      showSuccess(result.message || 'Enrollment reverted');
      loadRegistrationDetails(selectedRegistration.RegistrationID);
      loadRegistrations();
      loadStats();
    } catch (err) {
      showError('Revert failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateDocument = async (docId, isReceived) => {
    try {
      await apiCall(`/registration/documents/${docId}`, {
        method: 'PUT',
        body: JSON.stringify({ isReceived })
      });
      loadRegistrationDetails(selectedRegistration.RegistrationID);
    } catch (err) {
      showError('Failed to update document: ' + err.message);
    }
  };

  const handleUpdateInstallment = async () => {
    try {
      // Backend expects camelCase fields; the form holds the PascalCase row
      await apiCall(`/registration/payment-plan/${installmentForm.PlanItemID}`, {
        method: 'PUT',
        body: JSON.stringify({
          installmentName: installmentForm.InstallmentName,
          dueDate: installmentForm.DueDate ? installmentForm.DueDate.split('T')[0] : null,
          amountUSD: installmentForm.AmountUSD || 0,
          amountLBP: installmentForm.AmountLBP || 0,
          notes: installmentForm.CustomNotes || null
        })
      });
      showSuccess('Installment updated');
      setShowEditInstallment(false);
      loadRegistrationDetails(selectedRegistration.RegistrationID);
      loadTuitionRemaining(selectedRegistration.RegistrationID);
    } catch (err) {
      showError('Failed to update installment: ' + err.message);
    }
  };

  const handleAddInstallment = async () => {
    if (!addInstallmentForm.installmentName || !addInstallmentForm.dueDate) {
      showError('Installment needs a name and a due date');
      return;
    }
    try {
      await apiCall(`/registration/${selectedRegistration.RegistrationID}/payment-plan`, {
        method: 'POST',
        body: JSON.stringify(addInstallmentForm)
      });
      showSuccess('Installment added');
      setShowAddInstallment(false);
      setAddInstallmentForm({ installmentName: '', dueDate: '', amountUSD: 0, amountLBP: 0, notes: '' });
      loadRegistrationDetails(selectedRegistration.RegistrationID);
      loadTuitionRemaining(selectedRegistration.RegistrationID);
    } catch (err) {
      showError('Failed to add installment: ' + err.message);
    }
  };

  const handleDeleteInstallment = async (planItemId) => {
    if (!window.confirm('Delete this installment? Its amount will be split evenly across the remaining unpaid months.')) return;
    try {
      await apiCall(`/registration/payment-plan/${planItemId}`, { method: 'DELETE' });
      showSuccess('Installment deleted');
      loadRegistrationDetails(selectedRegistration.RegistrationID);
      loadTuitionRemaining(selectedRegistration.RegistrationID);
    } catch (err) {
      showError('Failed to delete installment: ' + err.message);
    }
  };

  // How much tuition still needs scheduling (owed − what SIS already covers −
  // what paid installments hold). Feeds the "Remaining to schedule" readout.
  const loadTuitionRemaining = useCallback(async (regId) => {
    try {
      setTuitionRemaining(await apiCall(`/registration/${regId}/tuition-remaining`));
    } catch (err) {
      setTuitionRemaining(null);
      console.error('Failed to load tuition remaining:', err);
    }
  }, [apiCall]);

  // Generate the plan from the chosen date range (monthly installments) for the
  // remaining tuition. Unpaid installments are replaced; paid ones are kept.
  const handleGenerateSchedule = async () => {
    if (!selectedRegistration?.RegistrationID) return;
    if (!scheduleForm.startDate || !scheduleForm.endDate) {
      showError('Pick a start and end date for the schedule');
      return;
    }
    if (scheduleForm.startDate > scheduleForm.endDate) {
      showError('Start date must be on or before the end date');
      return;
    }
    if (!window.confirm('Generate the schedule? Unpaid installments are replaced; installments with recorded payments are kept.')) return;
    try {
      setGeneratingPlan(true);
      const r = await apiCall(`/registration/${selectedRegistration.RegistrationID}/payment-plan/generate`, {
        method: 'PUT',
        body: JSON.stringify(scheduleForm)
      });
      showSuccess(r.message || 'Schedule generated');
      loadRegistrationDetails(selectedRegistration.RegistrationID);
      loadTuitionRemaining(selectedRegistration.RegistrationID);
    } catch (err) {
      showError('Failed to generate schedule: ' + err.message);
    } finally {
      setGeneratingPlan(false);
    }
  };

  // ============================================
  // HELPERS
  // ============================================
  const getStatusColor = (status) => {
    const colors = {
      'Pending': 'bg-yellow-100 text-yellow-800',
      'DocumentsReceived': 'bg-blue-100 text-blue-800',
      'UnderReview': 'bg-purple-100 text-purple-800',
      'Accepted': 'bg-green-100 text-green-800',
      'PartialPayment': 'bg-orange-100 text-orange-800',
      'FullyPaid': 'bg-emerald-100 text-emerald-800',
      'Enrolled': 'bg-teal-100 text-teal-800',
      'Rejected': 'bg-red-100 text-red-800',
      'Cancelled': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'Pending': <Clock size={16} />,
      'Accepted': <CheckCircle size={16} />,
      'PartialPayment': <DollarSign size={16} />,
      'FullyPaid': <CheckCircle size={16} />,
      'Enrolled': <GraduationCap size={16} />,
      'Rejected': <XCircle size={16} />
    };
    return icons[status] || <Clock size={16} />;
  };

  const formatCurrency = (amount, currency) => {
    if (currency === 'USD') return `$${Number(amount || 0).toLocaleString()}`;
    return `${Number(amount || 0).toLocaleString()} LL`;
  };

  const filteredRegistrations = registrations.filter(r => {
    const matchesSearch = searchTerm === '' || 
      r.FatherName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.MotherName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.RegistrationNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.FamilyID?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || r.RegistrationType === typeFilter;
    return matchesSearch && matchesType;
  });

  // ============================================
  // RENDER: REGISTRATION LIST
  // ============================================
  const renderList = () => (
    <div>
      {/* Stats header */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-xs text-gray-500 mb-1">Registrations — {registrationYear}</p>
            <p className="text-2xl font-bold text-gray-800">{stats.TotalRegistrations || 0}</p>
            <p className="text-xs text-gray-500">{stats.TotalChildren || 0} children</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-xs text-gray-500 mb-1">Pending</p>
            <p className="text-2xl font-bold text-yellow-600">{stats.Pending || 0}</p>
            <p className="text-xs text-gray-500">{stats.Rejected || 0} rejected</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-xs text-gray-500 mb-1">Accepted / Enrolled</p>
            <p className="text-2xl font-bold text-green-700">
              {stats.Accepted || 0} <span className="text-gray-300 font-normal">/</span> {stats.Enrolled || 0}
            </p>
            <p className="text-xs text-gray-500">{stats.PartialPayment || 0} partial · {stats.FullyPaid || 0} paid</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-xs text-gray-500 mb-1">Type</p>
            <p className="text-2xl font-bold text-blue-700">{stats.ReRegistrations || 0}</p>
            <p className="text-xs text-gray-500">re-registrations · {stats.NewFamilies || 0} new families</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4 col-span-2 md:col-span-1">
            <p className="text-xs text-gray-500 mb-1">Collected (LBP)</p>
            <p className="text-lg font-bold text-gray-800">
              {(stats.TotalPaidLBP || 0).toLocaleString()} <span className="text-xs font-normal text-gray-400">/ {(stats.TotalExpectedLBP || 0).toLocaleString()} LL</span>
            </p>
            <div className="w-full bg-gray-100 rounded-full h-2 mt-2">
              <div
                className="bg-emerald-500 h-2 rounded-full"
                style={{ width: `${Math.min(100, stats.TotalExpectedLBP > 0 ? (stats.TotalPaidLBP / stats.TotalExpectedLBP) * 100 : 0)}%` }}
              />
            </div>
            {(stats.TotalExpectedUSD > 0 || stats.TotalPaidUSD > 0) && (
              <p className="text-xs text-gray-500 mt-1">
                USD: ${(stats.TotalPaidUSD || 0).toLocaleString()} / ${(stats.TotalExpectedUSD || 0).toLocaleString()}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search by name, family ID, or registration #..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Statuses</option>
          <option value="Pending">Pending</option>
          <option value="Accepted">Accepted</option>
          <option value="PartialPayment">Partial Payment</option>
          <option value="FullyPaid">Fully Paid</option>
          <option value="Enrolled">Enrolled</option>
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Types</option>
          <option value="New">New Families</option>
          <option value="ReRegistration">Re-Registration</option>
        </select>
        <Gate permission="registration.re_registration.create" label="Re-Register Family">
          <button
            onClick={() => { loadEligibleFamilies(); setShowReRegistration(true); }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <RefreshCw size={18} />
            Re-Register Family
          </button>
        </Gate>
        <Gate permission="registration.create_registration.create" label="New Family Registration">
          <button
            onClick={() => setShowNewRegistration(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
          >
            <UserPlus size={18} />
            New Family
          </button>
        </Gate>
        <Gate permission="registration.view_financial.view" label="Overdue Bills view">
          <button
            onClick={() => { loadOverdue(); setActiveTab('overdue'); }}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
          >
            <AlertTriangle size={18} />
            Overdue Bills
          </button>
        </Gate>
        <Gate permission="registration.configure_pricing.view" label="Tuition Pricing view">
          <button
            onClick={() => { loadPricing(); setActiveTab('pricing'); }}
            className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-2"
          >
            <DollarSign size={18} />
            Tuition Pricing
          </button>
        </Gate>
        <Gate adminOnly label="Manage Dropdown Lists">
          <button
            onClick={() => setShowLookupsManager(true)}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2"
            title="Religions, marital statuses, employment statuses, job sectors..."
          >
            <ClipboardList size={18} />
            Lists
          </button>
        </Gate>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reg #</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Family</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Children</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredRegistrations.map(reg => (
              <tr key={reg.RegistrationID} className="hover:bg-gray-50">
                <td
                  className="px-4 py-3 text-sm font-medium text-blue-600 cursor-pointer hover:underline"
                  onClick={() => { setDetailView('family'); loadRegistrationDetails(reg.RegistrationID); setActiveTab('detail'); }}
                >
                  {reg.RegistrationNumber}
                </td>
                <td className="px-4 py-3">
                  <div className="text-sm font-medium text-gray-900">{reg.FatherName}</div>
                  <div className="text-xs text-gray-500">{reg.FamilyID || 'New Family'}</div>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                    reg.RegistrationType === 'New' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                  }`}>
                    {reg.RegistrationType}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-center">
                  <span className="inline-flex items-center justify-center w-6 h-6 bg-gray-100 rounded-full text-xs font-semibold text-gray-700">
                    {reg.ChildrenCount}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-right whitespace-nowrap">
                  {(reg.FinalTotalLBP || 0) > 0 && <div>{(reg.FinalTotalLBP || 0).toLocaleString()} LL</div>}
                  {(reg.FinalTotalUSD || 0) > 0 && <div className="text-gray-500">${(reg.FinalTotalUSD || 0).toLocaleString()}</div>}
                  {!(reg.FinalTotalLBP || reg.FinalTotalUSD) && <span className="text-gray-400">—</span>}
                </td>
                <td className="px-4 py-3 text-xs text-right whitespace-nowrap">
                  {(reg.BalanceLBP || 0) > 0 || (reg.BalanceUSD || 0) > 0 ? (
                    <span className="text-red-600 font-medium">
                      {(reg.BalanceLBP || 0) > 0 && <div>{(reg.BalanceLBP || 0).toLocaleString()} LL</div>}
                      {(reg.BalanceUSD || 0) > 0 && <div>${(reg.BalanceUSD || 0).toLocaleString()}</div>}
                    </span>
                  ) : (
                    <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700">settled</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full ${getStatusColor(reg.Status)}`}>
                    {getStatusIcon(reg.Status)}
                    {reg.Status}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => { setDetailView('family'); loadRegistrationDetails(reg.RegistrationID); setActiveTab('detail'); }}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <Eye size={18} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredRegistrations.length === 0 && (
          <div className="text-center py-8 text-gray-500">No registrations found</div>
        )}
      </div>
    </div>
  );

  // ============================================
  // RENDER: REGISTRATION DETAIL
  // ============================================
  const renderDetail = () => {
    if (!registrationDetails) return <div className="text-center py-8">Loading...</div>;

    const { registration, children, fees, paymentPlan, documents, pledgeDiscounts } = registrationDetails;

    // The scheduled installments no longer add up to the remaining balance
    // (someone edited an amount). Block printing the pledge until it's resolved.
    const planMismatch = !!(tuitionRemaining && tuitionRemaining.planMatchesRemaining === false);
    const fmtMoney = (usd, lbp) => [
      usd > 0 ? `$${Number(usd).toLocaleString()}` : null,
      lbp > 0 ? `${Number(lbp).toLocaleString()} LL` : null
    ].filter(Boolean).join(' + ') || '$0';

    return (
      <div>
        {/* Back Button & Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => { setActiveTab('list'); setRegistrationDetails(null); }}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ChevronLeft size={24} />
          </button>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-gray-800">{registration.RegistrationNumber}</h2>
            <p className="text-gray-600">{registration.FatherName} - {registration.FamilyID || 'New Family'}</p>
          </div>
          <span className={`px-3 py-1 rounded-full text-sm ${getStatusColor(registration.Status)}`}>
            {registration.Status}
          </span>
        </div>

        {/* View switch: family details vs everything financial */}
        <div className="flex gap-2 mb-6">
          <Gate permission="registration.view_registrations.view" label="Family tab">
            <button
              onClick={() => setDetailView('family')}
              className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 ${
                detailView === 'family' ? 'bg-blue-600 text-white' : 'bg-white border text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Home size={16} /> Family
            </button>
          </Gate>
          <Gate permission="registration.view_financial.view" label="Financial tab">
            <button
              onClick={() => setDetailView('financial')}
              className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 ${
                detailView === 'financial' ? 'bg-green-600 text-white' : 'bg-white border text-gray-700 hover:bg-gray-50'
              }`}
            >
              <DollarSign size={16} /> Financial
            </button>
          </Gate>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Family Info & Children */}
          <div className="lg:col-span-2 space-y-6">
            {detailView === 'family' && (<>
            {/* Family Info Card */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Home size={20} />
                  Family Information
                  {familyDetails?.FamilyStatus && (
                    <span className="ml-1 bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded-full text-xs font-medium">{familyDetails.FamilyStatus}</span>
                  )}
                </h3>
                {familyDetails && (
                  <Gate permission="registration.family_reports.view" label="Family Report">
                    <button
                      onClick={() => setReportMode('family')}
                      className="px-3 py-1 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 mr-2"
                    >
                      Report
                    </button>
                  </Gate>
                )}
                {familyDetails && (
                  <Gate permission="registration.manage_family_details.edit" label="Edit Family Details">
                  <button
                    onClick={() => {
                      setFamilyForm({
                        fatherName: familyDetails.FatherName || '',
                        motherName: familyDetails.MotherName || '',
                        fatherPhone: familyDetails.FatherPhone || familyDetails.PrimaryPhone || '',
                        motherPhone: familyDetails.MotherPhone || familyDetails.SecondaryPhone || '',
                        email: familyDetails.Email || '',
                        address: familyDetails.Address || '',
                        religion: familyDetails.Religion || '',
                        maritalStatus: familyDetails.MaritalStatus || '',
                        fatherJob: familyDetails.FatherJob || '',
                        motherJob: familyDetails.MotherJob || '',
                        fatherStatus: familyDetails.FatherStatus || '',
                        motherStatus: familyDetails.MotherStatus || '',
                        fatherJobSector: familyDetails.FatherJobSector || '',
                        motherJobSector: familyDetails.MotherJobSector || '',
                        fatherIsAlumni: !!familyDetails.FatherIsAlumni,
                        motherIsAlumni: !!familyDetails.MotherIsAlumni,
                        sisNumber: familyDetails.SISNumber || '',
                        familyStatus: familyDetails.FamilyStatus || '',
                        // Arabic names (each parent's own first + last)
                        fatherFirstNameAr: familyDetails.FatherFirstNameAr || '',
                        fatherLastNameAr: familyDetails.FatherLastNameAr || '',
                        motherFirstNameAr: familyDetails.MotherFirstNameAr || '',
                        motherLastNameAr: familyDetails.MotherLastNameAr || '',
                        // Dual nationality per parent
                        fatherNationality1: familyDetails.FatherNationality1 || '',
                        fatherNationality2: familyDetails.FatherNationality2 || '',
                        motherNationality1: familyDetails.MotherNationality1 || '',
                        motherNationality2: familyDetails.MotherNationality2 || '',
                        // Deceased flags
                        fatherDeceased: !!familyDetails.FatherDeceased,
                        motherDeceased: !!familyDetails.MotherDeceased,
                        // Job address
                        fatherCompany: familyDetails.FatherCompany || '',
                        fatherWorkAddress: familyDetails.FatherWorkAddress || '',
                        motherCompany: familyDetails.MotherCompany || '',
                        motherWorkAddress: familyDetails.MotherWorkAddress || '',
                        // Lebanese registry
                        registrationPlaceId: familyDetails.RegistrationPlaceID || '',
                        registerNumber: familyDetails.RegisterNumber || ''
                      });
                      // Seed the cascading geo picker to the family's saved place.
                      initGeoForFamily(
                        familyDetails.PlaceGovernorateID || '',
                        familyDetails.PlaceCazaID || '',
                        familyDetails.RegistrationPlaceID || ''
                      );
                      setShowFamilyEdit(true);
                    }}
                    className="px-3 py-1 border border-blue-300 text-blue-700 text-sm rounded-lg hover:bg-blue-50"
                  >
                    Edit Details
                  </button>
                  </Gate>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2 text-sm">
                <div>
                  <label className="text-xs text-gray-500">Father's Name</label>
                  <p className="font-medium">{(familyDetails?.FatherName ?? registration.FatherName) || '-'}</p>
                  {(familyDetails?.FatherFirstNameAr || familyDetails?.FatherLastNameAr) && (
                    <p className="text-xs text-gray-500" dir="rtl">{[familyDetails.FatherFirstNameAr, familyDetails.FatherLastNameAr].filter(Boolean).join(' ')}</p>
                  )}
                  {(familyDetails?.FatherNationality1 || familyDetails?.FatherNationality2) && (
                    <p className="text-xs text-gray-400">{[familyDetails.FatherNationality1, familyDetails.FatherNationality2].filter(Boolean).join(' / ')}</p>
                  )}
                </div>
                <div>
                  <label className="text-xs text-gray-500">Father's Phone</label>
                  <p className="font-medium flex items-center gap-1"><Phone size={14} />{familyDetails?.FatherPhone || familyDetails?.PrimaryPhone || registration.Phone || '-'}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Father's Job</label>
                  <p className="font-medium">
                    {familyDetails?.FatherJob || '-'}
                    {familyDetails?.FatherJobSector && <span className="text-xs text-gray-500"> ({familyDetails.FatherJobSector})</span>}
                  </p>
                  {(familyDetails?.FatherCompany || familyDetails?.FatherWorkAddress) && (
                    <p className="text-xs text-gray-500">{[familyDetails.FatherCompany, familyDetails.FatherWorkAddress].filter(Boolean).join(' · ')}</p>
                  )}
                  <p className="text-xs text-gray-500">
                    {familyDetails?.FatherStatus || ''}
                    {!!familyDetails?.FatherIsAlumni && <span className="ml-1 bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded text-xs">Old Student</span>}
                    {!!familyDetails?.FatherDeceased && <span className="ml-1 bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded text-xs">Deceased</span>}
                  </p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Mother's Name</label>
                  <p className="font-medium">{(familyDetails?.MotherName ?? registration.MotherName) || '-'}</p>
                  {(familyDetails?.MotherFirstNameAr || familyDetails?.MotherLastNameAr) && (
                    <p className="text-xs text-gray-500" dir="rtl">{[familyDetails.MotherFirstNameAr, familyDetails.MotherLastNameAr].filter(Boolean).join(' ')}</p>
                  )}
                  {(familyDetails?.MotherNationality1 || familyDetails?.MotherNationality2) && (
                    <p className="text-xs text-gray-400">{[familyDetails.MotherNationality1, familyDetails.MotherNationality2].filter(Boolean).join(' / ')}</p>
                  )}
                </div>
                <div>
                  <label className="text-xs text-gray-500">Mother's Phone</label>
                  <p className="font-medium flex items-center gap-1"><Phone size={14} />{familyDetails?.MotherPhone || familyDetails?.SecondaryPhone || '-'}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Mother's Job</label>
                  <p className="font-medium">
                    {familyDetails?.MotherJob || '-'}
                    {familyDetails?.MotherJobSector && <span className="text-xs text-gray-500"> ({familyDetails.MotherJobSector})</span>}
                  </p>
                  {(familyDetails?.MotherCompany || familyDetails?.MotherWorkAddress) && (
                    <p className="text-xs text-gray-500">{[familyDetails.MotherCompany, familyDetails.MotherWorkAddress].filter(Boolean).join(' · ')}</p>
                  )}
                  <p className="text-xs text-gray-500">
                    {familyDetails?.MotherStatus || ''}
                    {!!familyDetails?.MotherIsAlumni && <span className="ml-1 bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded text-xs">Old Student</span>}
                    {!!familyDetails?.MotherDeceased && <span className="ml-1 bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded text-xs">Deceased</span>}
                  </p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Email</label>
                  <p className="font-medium flex items-center gap-1"><Mail size={14} />{(familyDetails?.Email ?? registration.Email) || '-'}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Religion</label>
                  <p className="font-medium">{familyDetails?.Religion || '-'}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Marital Status</label>
                  <p className="font-medium">{familyDetails?.MaritalStatus || '-'}</p>
                </div>
                <div className="col-span-2 md:col-span-3">
                  <label className="text-xs text-gray-500">Address</label>
                  <p className="font-medium">{familyDetails?.Address || '-'}</p>
                </div>
                <div className="col-span-2 md:col-span-3">
                  <label className="text-xs text-gray-500">Lebanese Registry — القيد</label>
                  <p className="font-medium">
                    {[familyDetails?.GovernorateNameAr, familyDetails?.CazaNameAr, familyDetails?.LocalityNameAr].filter(Boolean).join(' / ') || '-'}
                    {familyDetails?.RegisterNumber && <span className="text-xs text-gray-500"> — رقم السجل {familyDetails.RegisterNumber}</span>}
                  </p>
                </div>
              </div>
            </div>

            {/* Children Card */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Users size={20} />
                  Children ({children.length})
                </h3>
                <Gate permission="registration.manage_children.create" label="Add Child">
                  <button
                    onClick={openAddChild}
                    className="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center gap-1"
                  >
                    <Plus size={16} /> Add Child
                  </button>
                </Gate>
              </div>
              <div className="space-y-3">
                {children.map(child => (
                  <div key={child.ChildID} className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">{child.FullName}</p>
                      <p className="text-sm text-gray-500">
                        {child.ApplyingForClass} • {child.IsNewStudent ? 'New Student' : 'Returning'} 
                        {child.StudentID && ` • ${child.StudentID}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">
                        ${child.ChildTotalUSD?.toLocaleString()} + {child.ChildTotalLBP?.toLocaleString()} LL
                      </span>
                      <Gate permission="registration.manage_children.create" label="Change Class">
                        <button
                          onClick={() => openChangeClass(child)}
                          className="text-blue-500 hover:text-blue-700"
                          title="Change class (re-prices fees)"
                        >
                          <Edit2 size={16} />
                        </button>
                      </Gate>
                      <Gate permission="registration.manage_children.delete" label="Remove Child">
                        <button
                          onClick={() => handleRemoveChild(child.ChildID)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 size={16} />
                        </button>
                      </Gate>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            </>)}

            {detailView === 'financial' && (<>
            {/* Fees Card */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <FileText size={20} />
                Fees Breakdown
              </h3>
              <div className="space-y-2">
                {[...children.map(c => ({ key: c.ChildID, label: `${c.FullName}`, sub: c.ApplyingForClass, rows: fees.filter(f => f.ChildID === c.ChildID) })),
                  ...(fees.some(f => !f.ChildID) ? [{ key: 'family', label: 'Family Fees', sub: null, rows: fees.filter(f => !f.ChildID) }] : [])
                ].map(group => {
                  const isOpen = expandedFeeChildren.includes(group.key);
                  const totLBP = group.rows.filter(f => f.Currency === 'LBP').reduce((s, f) => s + (f.FinalAmount || 0), 0);
                  const totUSD = group.rows.filter(f => f.Currency === 'USD').reduce((s, f) => s + (f.FinalAmount || 0), 0);
                  return (
                    <div key={group.key} className="border border-gray-200 rounded-lg overflow-hidden">
                      <button
                        onClick={() => setExpandedFeeChildren(x =>
                          x.includes(group.key) ? x.filter(k => k !== group.key) : [...x, group.key])}
                        className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 text-left"
                      >
                        <span className="flex items-center gap-2 text-sm font-medium text-gray-800">
                          <span className={`inline-block transition-transform text-gray-400 ${isOpen ? 'rotate-90' : ''}`}>▸</span>
                          {group.label}
                          {group.sub && <span className="text-xs font-normal text-gray-500">→ {group.sub}</span>}
                          <span className="text-xs font-normal text-gray-400">({group.rows.length} fees)</span>
                        </span>
                        <span className="text-xs font-semibold text-gray-700 whitespace-nowrap">
                          {totLBP > 0 && `${totLBP.toLocaleString()} LL`}
                          {totLBP > 0 && totUSD > 0 && ' + '}
                          {totUSD > 0 && `$${totUSD.toLocaleString()}`}
                        </span>
                      </button>
                      {isOpen && (
                        <table className="w-full text-xs">
                          <tbody>
                            {group.rows.map(fee => (
                              <tr key={fee.FeeID} className="border-t border-gray-100">
                                <td className="py-1.5 px-3">{fee.FeeTypeName}</td>
                                <td className="py-1.5 px-2 text-right text-gray-500">
                                  {fee.DiscountAmount > 0 && (
                                    <span className="text-green-600">
                                      {formatCurrency(fee.OriginalAmount, fee.Currency)} −{formatCurrency(fee.DiscountAmount, fee.Currency)}
                                    </span>
                                  )}
                                </td>
                                <td className="py-1.5 px-3 text-right font-medium w-32">{formatCurrency(fee.FinalAmount, fee.Currency)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  );
                })}
                {children.length > 1 && (
                  <button
                    onClick={() => setExpandedFeeChildren(
                      expandedFeeChildren.length ? [] : children.map(c => c.ChildID).concat(fees.some(f => !f.ChildID) ? ['family'] : [])
                    )}
                    className="text-xs text-blue-600 hover:underline"
                  >
                    {expandedFeeChildren.length ? 'Collapse all' : 'Expand all'}
                  </button>
                )}
              </div>
            </div>

            {/* Payment Plan — wide column so installments stay one line each */}
            <div className="bg-white rounded-lg shadow p-5">
              <div className="flex flex-wrap justify-between items-center gap-2 mb-3">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Calendar size={20} />
                  Payment Plan
                  {registration.HasCustomPlan && <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">Custom</span>}
                </h3>
                <div className="flex items-center gap-2">
                  <Gate permission="registration.manage_payment_plan.create" label="Add Installment">
                    <button
                      onClick={() => setShowAddInstallment(true)}
                      className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 flex items-center gap-1"
                    >
                      <Plus size={13} /> Add Month
                    </button>
                  </Gate>
                  {/* Reprint — reprints the saved pledge exactly; no editing, so
                      it's available even where Print (which can edit/save a new
                      pledge) is gated. */}
                  <button
                    onClick={() => { setPledgeAllowEdit(false); setShowPrintPlan(true); }}
                    className="px-3 py-1.5 border border-gray-300 text-gray-700 text-xs rounded-lg hover:bg-gray-50 flex items-center gap-1"
                    title="Reprint the saved pledge exactly"
                  >
                    <RotateCcw size={13} /> Reprint
                  </button>
                  <Gate permission="registration.manage_payment_plan.edit" label="Print Pledge">
                    <button
                      onClick={() => { setPledgeAllowEdit(true); setShowPrintPlan(true); }}
                      disabled={!paymentPlan.length || planMismatch}
                      className="px-3 py-1.5 border border-gray-300 text-gray-700 text-xs rounded-lg hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1"
                      title={planMismatch
                        ? "Scheduled total doesn't match the remaining balance — fix the installments first"
                        : 'Print the parent pledge (تعهد)'}
                    >
                      <Printer size={13} /> Print
                    </button>
                  </Gate>
                </div>
              </div>

              {/* Who built / last edited the schedule */}
              {(registration.PlanGeneratedByName || registration.PlanLastEditedByName) && (
                <div className="mb-3 text-xs text-gray-500 flex flex-wrap gap-x-4 gap-y-0.5">
                  {registration.PlanGeneratedByName && (
                    <span>Created by <b className="text-gray-700">{registration.PlanGeneratedByName}</b>
                      {registration.PlanGeneratedAt && ` · ${new Date(registration.PlanGeneratedAt).toLocaleString()}`}</span>
                  )}
                  {registration.PlanLastEditedByName && (
                    <span>Last edited by <b className="text-gray-700">{registration.PlanLastEditedByName}</b>
                      {registration.PlanLastEditedAt && ` · ${new Date(registration.PlanLastEditedAt).toLocaleString()}`}</span>
                  )}
                </div>
              )}

              {/* Scheduled-vs-remaining check — printing is blocked until they match */}
              {planMismatch && (
                <div className="mb-3 p-2.5 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 flex items-start gap-2">
                  <AlertTriangle size={15} className="mt-0.5 flex-shrink-0" />
                  <span>
                    The scheduled installments total{' '}
                    <b>{fmtMoney(tuitionRemaining.scheduledUSD, tuitionRemaining.scheduledLBP)}</b>,
                    but the remaining balance is{' '}
                    <b>{fmtMoney(tuitionRemaining.expectedUSD, tuitionRemaining.expectedLBP)}</b>.
                    Adjust the installments so they match — the pledge can't be printed until they do.
                  </span>
                </div>
              )}

              {/* Schedule generator — pick a date range; the remaining tuition
                  (owed − what SIS covers) is spread monthly across it. */}
              <Gate permission="registration.manage_payment_plan.edit" label="Generate Payment Schedule">
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex flex-wrap items-end gap-2">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">From</label>
                      <input
                        type="date"
                        value={scheduleForm.startDate}
                        onChange={e => setScheduleForm(f => ({ ...f, startDate: e.target.value }))}
                        className="border rounded-lg px-2 py-1.5 text-xs"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">To</label>
                      <input
                        type="date"
                        value={scheduleForm.endDate}
                        onChange={e => setScheduleForm(f => ({ ...f, endDate: e.target.value }))}
                        className="border rounded-lg px-2 py-1.5 text-xs"
                      />
                    </div>
                    <label className="flex items-center gap-1.5 text-xs text-gray-700 pb-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={scheduleForm.allUSD}
                        onChange={e => setScheduleForm(f => ({ ...f, allUSD: e.target.checked }))}
                        className="rounded"
                      />
                      All in USD <span className="text-gray-400">(@ {usdRate.toLocaleString()} LL)</span>
                    </label>
                    <button
                      onClick={handleGenerateSchedule}
                      disabled={generatingPlan}
                      className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                    >
                      <Calendar size={13} /> {generatingPlan ? 'Generating…' : 'Generate Schedule'}
                    </button>
                  </div>
                  {tuitionRemaining && (
                    <div className="mt-2 text-xs text-gray-600 flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span className="font-medium text-gray-700">Remaining to schedule:</span>
                      {scheduleForm.allUSD ? (
                        <span className="font-semibold text-blue-700">
                          ${tuitionRemaining.remainingAsUSD.toLocaleString()}
                        </span>
                      ) : (
                        <>
                          {tuitionRemaining.remainingUSD > 0 &&
                            <span className="font-semibold text-blue-700">${tuitionRemaining.remainingUSD.toLocaleString()}</span>}
                          {tuitionRemaining.remainingLBP > 0 &&
                            <span className="font-semibold text-blue-700">{tuitionRemaining.remainingLBP.toLocaleString()} LL</span>}
                          {tuitionRemaining.remainingUSD <= 0 && tuitionRemaining.remainingLBP <= 0 &&
                            <span className="text-gray-400">nothing outstanding</span>}
                        </>
                      )}
                      {!tuitionRemaining.reachable &&
                        <span className="text-amber-600">· SIS unreachable — scheduling full tuition</span>}
                    </div>
                  )}
                </div>
              </Gate>

              {paymentPlan.length > 0 && (() => {
                const totLBP = paymentPlan.reduce((s, i) => s + (i.AmountLBP || 0), 0);
                const paidLBP = paymentPlan.reduce((s, i) => s + (i.PaidLBP || 0), 0);
                const paidCount = paymentPlan.filter(i => i.Status === 'Paid').length;
                return (
                  <div className="mb-3">
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>{paidCount} of {paymentPlan.length} installments paid</span>
                      {totLBP > 0 && <span>{paidLBP.toLocaleString()} / {totLBP.toLocaleString()} LL</span>}
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5">
                      <div
                        className="bg-emerald-500 h-1.5 rounded-full"
                        style={{ width: `${totLBP > 0 ? Math.min(100, (paidLBP / totLBP) * 100) : (paidCount / paymentPlan.length) * 100}%` }}
                      />
                    </div>
                  </div>
                );
              })()}

              {paymentPlan.length > 0 ? (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b text-gray-500">
                      <th className="text-left py-1.5 pr-2 w-8">#</th>
                      <th className="text-left py-1.5 pr-2">Installment</th>
                      <th className="text-left py-1.5 pr-2">Due</th>
                      <th className="text-right py-1.5 pr-2">Amount</th>
                      <th className="text-right py-1.5 pr-2">Paid</th>
                      <th className="text-center py-1.5 pr-2">Status</th>
                      <th className="py-1.5 w-16"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentPlan.map(item => (
                      <tr
                        key={item.PlanItemID}
                        className={`border-b border-gray-100 ${
                          item.Status === 'Paid' ? 'bg-green-50/60' :
                          item.IsOverdue ? 'bg-red-50/60' : ''
                        }`}
                      >
                        <td className="py-1.5 pr-2 text-gray-400">{item.InstallmentNumber}</td>
                        <td className="py-1.5 pr-2 font-medium text-gray-800">{item.InstallmentName}</td>
                        <td className="py-1.5 pr-2 text-gray-500 whitespace-nowrap">{new Date(item.DueDate).toLocaleDateString()}</td>
                        <td className="py-1.5 pr-2 text-right whitespace-nowrap">
                          {item.AmountLBP > 0 && <div>{item.AmountLBP?.toLocaleString()} LL</div>}
                          {item.AmountUSD > 0 && <div className="text-gray-500">${item.AmountUSD?.toLocaleString()}</div>}
                          {!(item.AmountLBP || item.AmountUSD) && <span className="text-gray-300">—</span>}
                        </td>
                        <td className="py-1.5 pr-2 text-right whitespace-nowrap text-emerald-700">
                          {item.PaidLBP > 0 && <div>{item.PaidLBP?.toLocaleString()} LL</div>}
                          {item.PaidUSD > 0 && <div>${item.PaidUSD?.toLocaleString()}</div>}
                          {!(item.PaidLBP || item.PaidUSD) && <span className="text-gray-300">—</span>}
                        </td>
                        <td className="py-1.5 pr-2 text-center">
                          {item.Status === 'Paid' ? (
                            <span className="inline-flex px-1.5 py-0.5 rounded-full bg-green-100 text-green-700">paid</span>
                          ) : item.IsOverdue ? (
                            <span className="inline-flex px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">overdue</span>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                        <td className="py-1.5 text-right whitespace-nowrap">
                          <Gate permission="registration.manage_payment_plan.edit" label="Edit Installment">
                            <button
                              onClick={() => { setInstallmentForm(item); setShowEditInstallment(true); }}
                              className="text-blue-500 hover:text-blue-700 mr-2"
                              title="Edit"
                            >
                              <Edit2 size={13} />
                            </button>
                          </Gate>
                          <Gate permission="registration.manage_payment_plan.delete" label="Delete Installment">
                            <button
                              onClick={() => handleDeleteInstallment(item.PlanItemID)}
                              className="text-red-400 hover:text-red-600"
                              title="Delete"
                            >
                              <Trash2 size={13} />
                            </button>
                          </Gate>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-sm text-gray-500 italic text-center py-3">
                  No payment plan yet — apply a template or add months manually.
                </p>
              )}

              {/* Standby discounts — named lines shown on the pledge, netted out
                  of the schedule. Editing is its OWN permission
                  (registration.manage_standby_discounts.edit) so discount rights
                  can be granted without full plan-editing. Anyone who can view the
                  plan still sees a read-only list; only the editor's inputs/buttons
                  are gated. */}
              {!hasPermission('registration.manage_standby_discounts.edit') && (
                <PledgeDiscountsEditor
                  apiCall={apiCall}
                  registrationId={registration.RegistrationID}
                  discounts={pledgeDiscounts}
                  canEdit={false}
                  planInUSD={!!(tuitionRemaining?.planInUSD || scheduleForm.allUSD)}
                  usdRate={tuitionRemaining?.usdRate || usdRate}
                />
              )}
              <Gate permission="registration.manage_standby_discounts.edit" label="Standby Discounts">
                <PledgeDiscountsEditor
                  apiCall={apiCall}
                  registrationId={registration.RegistrationID}
                  discounts={pledgeDiscounts}
                  canEdit={true}
                  planInUSD={!!(tuitionRemaining?.planInUSD || scheduleForm.allUSD)}
                  usdRate={tuitionRemaining?.usdRate || usdRate}
                  onSaved={() => {
                    loadRegistrationDetails(registration.RegistrationID);
                    loadTuitionRemaining(registration.RegistrationID);
                  }}
                  showSuccess={showSuccess}
                  showError={showError}
                />
              </Gate>
            </div>
            </>)}

            {detailView === 'family' && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <ClipboardList size={20} />
                Required Documents
              </h3>
              {children.map(child => (
                <div key={child.ChildID} className="mb-4">
                  <p className="font-medium text-sm text-gray-600 mb-2">{child.FullName}</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {documents.filter(d => d.ChildID === child.ChildID).map(doc => (
                      <label
                        key={doc.DocID}
                        className={`flex items-center gap-2 p-2 rounded cursor-pointer ${
                          doc.IsReceived ? 'bg-green-50' : 'bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={doc.IsReceived}
                          onChange={(e) => handleUpdateDocument(doc.DocID, e.target.checked)}
                          className="rounded"
                        />
                        <span className="text-sm">{doc.DocTypeName}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            )}
          </div>

          {/* Right Column - Financial Summary & Actions */}
          <div className="space-y-6">
            {detailView === 'financial' && (<>
            {/* Financial Summary */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <DollarSign size={20} />
                Financial Summary
              </h3>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total USD</span>
                  <span className="font-medium">${registration.TotalUSD?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total LBP</span>
                  <span className="font-medium">{registration.TotalLBP?.toLocaleString()} LL</span>
                </div>
                {(registration.DiscountUSD > 0 || registration.DiscountLBP > 0) && (
                  <>
                    <hr />
                    <div className="flex justify-between text-green-600">
                      <span>Discount USD</span>
                      <span>-${registration.DiscountUSD?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-green-600">
                      <span>Discount LBP</span>
                      <span>-{registration.DiscountLBP?.toLocaleString()} LL</span>
                    </div>
                    <div className="text-xs text-gray-500">{registration.DiscountReason}</div>
                  </>
                )}
                <hr />
                <div className="flex justify-between text-blue-600">
                  <span>Paid USD</span>
                  <span>${registration.PaidUSD?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-blue-600">
                  <span>Paid LBP</span>
                  <span>{registration.PaidLBP?.toLocaleString()} LL</span>
                </div>
                <hr />
                <div className="flex justify-between font-bold text-lg">
                  <span>Balance USD</span>
                  <span className="text-red-600">${registration.BalanceUSD?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between font-bold">
                  <span>Balance LBP</span>
                  <span className="text-red-600">{registration.BalanceLBP?.toLocaleString()} LL</span>
                </div>
              </div>
            </div>

            {/* SIS Tuition Payments */}
            <div className="bg-white rounded-lg shadow p-5">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-base font-semibold flex items-center gap-2">
                  <CreditCard size={18} />
                  Tuition Payments (SIS)
                </h3>
                <div className="flex gap-1.5">
                  <button
                    onClick={loadSisPayments}
                    disabled={sisLoading || !registration.FamilyID}
                    className="px-3 py-1 bg-indigo-600 text-white text-xs rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {sisLoading ? 'Loading...' : 'Load'}
                  </button>
                  <Gate permission="registration.manage_payment_plan.edit" label="Apply SIS Payments to Plan">
                    <button
                      onClick={handleSisApply}
                      disabled={sisLoading || !registration.FamilyID}
                      className="px-3 py-1 bg-emerald-600 text-white text-xs rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                      title="Deduct registration/services, then mark installments paid from SIS payments"
                    >
                      Apply to Plan
                    </button>
                  </Gate>
                </div>
              </div>
              {familyDetails && (
                <div className="flex items-end gap-2 mb-3">
                  <div className="flex-1">
                    <label className="block text-xs text-gray-500 mb-1">SIS Number</label>
                    <input
                      type="text"
                      value={sisNumberInput}
                      onChange={e => setSisNumberInput(e.target.value)}
                      placeholder={registration.FamilyID}
                      className="w-full border rounded-lg px-2 py-1 text-xs"
                    />
                  </div>
                  <Gate permission="registration.manage_family_details.edit" label="Save SIS Link">
                    <button
                      onClick={handleSaveSisNumber}
                      disabled={sisLoading}
                      className="px-2 py-1 border border-indigo-300 text-indigo-700 text-xs rounded-lg hover:bg-indigo-50 disabled:opacity-50"
                    >
                      Save
                    </button>
                  </Gate>
                </div>
              )}
              {sisError && (
                <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg p-2 mb-2">{sisError}</p>
              )}
              {sisData && (
                sisData.payments.length > 0 ? (
                  <>
                    <div className="space-y-1 mb-2">
                      {sisData.payments.map((p, i) => (
                        <div key={i} className="flex justify-between items-center text-xs border-b border-gray-100 py-1">
                          <span className="text-gray-600">{new Date(p.date).toLocaleDateString()} <span className="text-gray-400">#{p.transactionId}</span></span>
                          <span className="font-medium">
                            {p.currency === 'LBP' ? `${p.amount.toLocaleString()} LL` : `$${p.amount.toLocaleString()}`}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-1.5 justify-end text-xs font-bold">
                      {Object.entries(sisData.totals).map(([cur, total]) => (
                        <span key={cur} className="bg-indigo-50 text-indigo-800 px-2 py-0.5 rounded-lg">
                          {cur === 'LBP' ? `${total.toLocaleString()} LL` : `$${total.toLocaleString()}`}
                        </span>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="text-xs text-gray-500 italic">
                    No {registrationYear} tuition payments in SIS for {sisData.sisNumber}.
                  </p>
                )
              )}
              {!sisData && !sisError && (
                <p className="text-xs text-gray-400">
                  Pulls this family's {registrationYear} tuition payments from SIS
                  (school network only). Adjust the SIS number if the IDs differ.
                </p>
              )}
            </div>

            {/* Actions — financial view only; family view is pure info */}
            <div className="bg-white rounded-lg shadow p-6 space-y-3">
              <h3 className="text-lg font-semibold mb-4">Actions</h3>

              <Gate permission="registration.apply_discounts.edit" label="Apply Discount">
                <button
                  onClick={() => {
                    setDiscountForm({
                      discountUSD: registration.DiscountUSD || 0,
                      discountLBP: registration.DiscountLBP || 0,
                      discountReason: registration.DiscountReason || ''
                    });
                    setShowDiscountModal(true);
                  }}
                  className="w-full py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center justify-center gap-2"
                >
                  <Percent size={18} /> Apply Discount
                </button>
              </Gate>

              <hr />

              {registration.Status === 'Pending' && (
                <Gate permission="registration.update_status.edit" label="Accept Registration">
                  <button
                    onClick={() => handleUpdateStatus('Accepted')}
                    className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Accept Registration
                  </button>
                </Gate>
              )}

              {(['Accepted', 'PartialPayment', 'FullyPaid'].includes(registration.Status) ||
                (registration.Status === 'Enrolled' && children.some(c => (c.Status || 'Pending') !== 'Enrolled'))) && (
                <Gate permission="registration.enroll.process" label="Enroll Students">
                  <button
                    onClick={handleEnroll}
                    className="w-full py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center justify-center gap-2"
                  >
                    <GraduationCap size={18} />{' '}
                    {registration.Status === 'Enrolled' ? 'Enroll Added Children' : 'Enroll Students'}
                  </button>
                </Gate>
              )}

              {registration.Status === 'Enrolled' && (
                <Gate permission="registration.enroll.process" label="Revert Enrollment">
                  <button
                    onClick={handleRevertEnrollment}
                    className="w-full py-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 flex items-center justify-center gap-2"
                  >
                    <RefreshCw size={16} /> Revert Enrollment
                  </button>
                </Gate>
              )}

              {registration.Status !== 'Enrolled' && registration.Status !== 'Rejected' && (
                <Gate permission="registration.update_status.edit" label="Reject Registration">
                  <button
                    onClick={() => handleUpdateStatus('Rejected')}
                    className="w-full py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
                  >
                    Reject
                  </button>
                </Gate>
              )}
            </div>
            </>)}
          </div>
        </div>
      </div>
    );
  };

  // ============================================
  // RENDER: OVERDUE BILLS REPORT
  // ============================================
  const renderOverdue = () => {
    const rows = overdueData || [];
    const totUSD = rows.reduce((s, r) => s + (r.OverdueUSD || 0), 0);
    const totLBP = rows.reduce((s, r) => s + (r.OverdueLBP || 0), 0);
    const fmtDate = d => d ? new Date(d).toLocaleDateString() : '-';

    const printOverdue = () => {
      const body = rows.map(r => `
        <tr>
          <td>${(r.FatherName || '').replace(/</g, '&lt;')} <span style="color:#888">(${r.FamilyID || ''})</span></td>
          <td>${r.Phone || ''}</td>
          <td style="text-align:center">${r.OverdueCount}</td>
          <td style="text-align:right">${r.OverdueUSD > 0 ? '$' + Number(r.OverdueUSD).toLocaleString() : ''}</td>
          <td style="text-align:right">${r.OverdueLBP > 0 ? Number(r.OverdueLBP).toLocaleString() + ' LL' : ''}</td>
          <td style="text-align:center">${fmtDate(r.OldestDueDate)}</td>
          <td style="text-align:center">${r.DaysOverdue}</td>
        </tr>`).join('');
      const w = window.open('', '', 'width=1000,height=700');
      w.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8">
        <title>Overdue Bills — ${registrationYear}</title>
        <style>@page{size:A4 landscape;margin:1.4cm;}*{box-sizing:border-box;}
        body{font-family:Arial,sans-serif;font-size:12px;color:#000;}
        h2{margin:0 0 4px;} .sub{color:#555;margin:0 0 12px;}
        table{width:100%;border-collapse:collapse;} th,td{border:1px solid #999;padding:5px 8px;}
        th{background:#f3f4f6;text-align:left;} tfoot td{font-weight:bold;background:#fafafa;}</style>
        </head><body>
        <h2>Overdue Bills — ${registrationYear}</h2>
        <p class="sub">${rows.length} famille(s) · printed ${new Date().toLocaleString()}</p>
        <table><thead><tr>
          <th>Family</th><th>Phone</th><th>#</th><th>Overdue USD</th><th>Overdue LBP</th><th>Oldest due</th><th>Days</th>
        </tr></thead><tbody>${body}</tbody>
        <tfoot><tr><td colspan="3">Total</td>
          <td style="text-align:right">$${totUSD.toLocaleString()}</td>
          <td style="text-align:right">${totLBP.toLocaleString()} LL</td>
          <td colspan="2"></td></tr></tfoot>
        </table></body></html>`);
      w.document.close(); w.focus();
      setTimeout(() => w.print(), 250);
    };

    return (
      <div>
        <div className="flex items-center gap-4 mb-6">
          <button onClick={() => setActiveTab('list')} className="p-2 hover:bg-gray-100 rounded-lg">
            <ChevronLeft size={24} />
          </button>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <AlertTriangle size={22} className="text-red-600" /> Overdue Bills — {registrationYear}
            </h2>
            <p className="text-sm text-gray-500">Families behind on their scheduled installments (unpaid portion only).</p>
          </div>
          <Gate permission="registration.manage_payment_plan.edit" label="Reconcile with SIS">
            <button onClick={handleReconcileSis} disabled={reconciling}
              className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1 text-sm"
              title="Apply SIS tuition payments to all plans, then refresh — paid families drop off">
              <RefreshCw size={15} className={reconciling ? 'animate-spin' : ''} /> {reconciling ? 'Reconciling…' : 'Reconcile with SIS'}
            </button>
          </Gate>
          <button onClick={loadOverdue} className="px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-1 text-sm">
            <RefreshCw size={15} /> Reload
          </button>
          <button onClick={printOverdue} disabled={!rows.length}
            className="px-3 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 disabled:opacity-50 flex items-center gap-1 text-sm">
            <Printer size={15} /> Print
          </button>
        </div>

        {!overdueData ? (
          <div className="text-center py-8 text-gray-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
            <CheckCircle size={32} className="mx-auto mb-2 text-emerald-500" />
            No overdue bills for {registrationYear}. 🎉
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-x-auto">
            <div className="flex flex-wrap gap-3 justify-end p-3 text-sm border-b bg-gray-50">
              <span className="text-gray-600">{rows.length} famille(s)</span>
              {totUSD > 0 && <span className="font-semibold text-red-600">${totUSD.toLocaleString()}</span>}
              {totLBP > 0 && <span className="font-semibold text-red-600">{totLBP.toLocaleString()} LL</span>}
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase">
                  <th className="px-4 py-2">Family</th>
                  <th className="px-4 py-2">Phone</th>
                  <th className="px-4 py-2 text-center">Months</th>
                  <th className="px-4 py-2 text-right">Overdue USD</th>
                  <th className="px-4 py-2 text-right">Overdue LBP</th>
                  <th className="px-4 py-2 text-center">Oldest due</th>
                  <th className="px-4 py-2 text-center">Days</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.RegistrationID} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-2">
                      <span className="font-medium text-gray-800">{r.FatherName}</span>
                      <span className="text-xs text-gray-400 ml-1">{r.FamilyID}</span>
                    </td>
                    <td className="px-4 py-2 text-gray-600">{r.Phone || '-'}</td>
                    <td className="px-4 py-2 text-center">{r.OverdueCount}</td>
                    <td className="px-4 py-2 text-right font-medium">{r.OverdueUSD > 0 ? `$${Number(r.OverdueUSD).toLocaleString()}` : ''}</td>
                    <td className="px-4 py-2 text-right font-medium">{r.OverdueLBP > 0 ? `${Number(r.OverdueLBP).toLocaleString()} LL` : ''}</td>
                    <td className="px-4 py-2 text-center text-gray-600">{fmtDate(r.OldestDueDate)}</td>
                    <td className="px-4 py-2 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${r.DaysOverdue > 30 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                        {r.DaysOverdue}d
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <button
                        onClick={() => { loadRegistrationDetails(r.RegistrationID); setDetailView('financial'); setActiveTab('detail'); }}
                        className="text-blue-600 text-xs hover:underline"
                      >
                        Open
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // ============================================
  // RENDER: TUITION PRICING ADMIN
  // ============================================
  const renderPricing = () => {
    const fmtAmt = (v, cur) => cur === 'LBP'
      ? `${(parseFloat(v) || 0).toLocaleString()} LL`
      : `$${(parseFloat(v) || 0).toLocaleString()}`;

    return (
      <div>
        <div className="flex items-center gap-4 mb-6">
          <button onClick={() => setActiveTab('list')} className="p-2 hover:bg-gray-100 rounded-lg">
            <ChevronLeft size={24} />
          </button>
          <div>
            <h2 className="text-xl font-bold text-gray-800">Tuition Pricing — {registrationYear}</h2>
            <p className="text-sm text-gray-500">Global fees apply to everyone; per-class rows override them for a class (and optionally only new or returning students).</p>
          </div>
        </div>

        {!pricingData ? (
          <div className="text-center py-8 text-gray-500">Loading pricing...</div>
        ) : (
          <div className="space-y-6">
            {/* Global USD → LBP rate used when a payment plan is set to all-USD */}
            <Gate permission="registration.configure_pricing.configure" label="USD Conversion Rate">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex flex-wrap items-end justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-semibold">USD → LBP Rate</h3>
                    <p className="text-xs text-gray-500 mt-1">
                      Used when a family's payment plan is consolidated into US dollars.
                      One rate for everyone.
                    </p>
                  </div>
                  <div className="flex items-end gap-2">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">LL per 1 USD</label>
                      <input
                        type="number"
                        value={usdRateInput}
                        onChange={e => setUsdRateInput(e.target.value)}
                        placeholder={String(usdRate)}
                        className="w-40 border rounded-lg px-2 py-1.5 text-sm text-right"
                      />
                    </div>
                    <button
                      onClick={handleSaveUsdRate}
                      className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                    >
                      Save Rate
                    </button>
                  </div>
                </div>
              </div>
            </Gate>

            {/* Global fees */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Global Fees</h3>
                <Gate permission="registration.configure_pricing.configure" label="Apply Pricing to Existing Registrations">
                  <button
                    onClick={handleSyncFees}
                    disabled={loading}
                    className="px-3 py-1.5 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1"
                    title="Add newly priced fees to families that already registered"
                  >
                    <RefreshCw size={14} /> Apply to Existing Registrations
                  </button>
                </Gate>
              </div>
              <p className="text-xs text-gray-500 mb-4">
                Pricing changes only affect <b>future</b> children/registrations automatically.
                After adding or pricing a fee, use "Apply to Existing Registrations" to add the
                missing fees to families already registered (existing fees, discounts, and
                payments are never modified).
              </p>

              {/* Add a variant (e.g. Registration fee for New students only) */}
              <div className="flex flex-wrap items-end gap-2 mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Fee Type</label>
                  <select
                    value={globalFeeForm.feeTypeId}
                    onChange={e => setGlobalFeeForm(f => ({ ...f, feeTypeId: e.target.value }))}
                    className="border rounded-lg px-2 py-1.5 text-sm"
                  >
                    <option value="">— fee —</option>
                    {feeTypes.map(ft => (
                      <option key={ft.FeeTypeID} value={ft.FeeTypeID}>{ft.FeeTypeName} ({ft.Currency})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Students</label>
                  <select
                    value={globalFeeForm.studentType}
                    onChange={e => setGlobalFeeForm(f => ({ ...f, studentType: e.target.value }))}
                    className="border rounded-lg px-2 py-1.5 text-sm"
                  >
                    <option value="All">All</option>
                    <option value="New">New only</option>
                    <option value="Returning">Returning only</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Amount</label>
                  <input
                    type="number"
                    value={globalFeeForm.amount}
                    onChange={e => setGlobalFeeForm(f => ({ ...f, amount: e.target.value }))}
                    className="w-32 border rounded-lg px-2 py-1.5 text-sm text-right"
                  />
                </div>
                <Gate permission="registration.configure_pricing.configure" label="Add Global Fee">
                  <button
                    onClick={handleAddGlobalFee}
                    className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center gap-1"
                  >
                    <Plus size={14} /> Add
                  </button>
                </Gate>
              </div>

              {pricingData.globalFees.length === 0 && (
                <p className="text-sm text-gray-500 italic">No global fees configured for {registrationYear}.</p>
              )}
              <div className="space-y-2">
                {pricingData.globalFees.map((fee, idx) => (
                  <div key={fee.GlobalFeeID} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium">{fee.FeeTypeName} <span className="text-xs text-gray-400">({fee.Currency})</span></p>
                      <p className="text-xs text-gray-500">{fee.Category}</p>
                    </div>
                    <select
                      value={fee.StudentType || 'All'}
                      onChange={e => setGlobalFeeField(idx, 'StudentType', e.target.value)}
                      className="border rounded-lg px-2 py-1.5 text-sm"
                    >
                      <option value="All">All</option>
                      <option value="New">New</option>
                      <option value="Returning">Returning</option>
                    </select>
                    <input
                      type="number"
                      value={fee.DefaultAmount ?? ''}
                      onChange={e => setGlobalFeeField(idx, 'DefaultAmount', e.target.value)}
                      className="w-36 border rounded-lg px-2 py-1.5 text-sm text-right"
                    />
                    <input
                      type="text"
                      value={fee.Notes || ''}
                      placeholder="Notes"
                      onChange={e => setGlobalFeeField(idx, 'Notes', e.target.value)}
                      className="w-44 border rounded-lg px-2 py-1.5 text-sm"
                    />
                    <Gate permission="registration.configure_pricing.edit" label="Edit Global Fee">
                      <button
                        onClick={() => handleSaveGlobalFee(fee)}
                        className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                      >
                        Save
                      </button>
                    </Gate>
                    <Gate permission="registration.configure_pricing.configure" label="Delete Global Fee">
                      <button
                        onClick={() => handleDeleteGlobalFee(fee)}
                        className="px-2 py-1.5 text-red-600 text-sm hover:bg-red-50 rounded-lg"
                        title="Remove this fee row"
                      >
                        <Trash2 size={15} />
                      </button>
                    </Gate>
                  </div>
                ))}
              </div>
            </div>

            {/* Per-class pricing */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Per-Class Pricing</h3>

              {/* Add row */}
              <div className="flex flex-wrap items-end gap-2 mb-5 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Class</label>
                  <select
                    value={classPricingForm.className}
                    onChange={e => setClassPricingForm(f => ({ ...f, className: e.target.value }))}
                    className="border rounded-lg px-2 py-1.5 text-sm"
                  >
                    <option value="">— class —</option>
                    {classLevels.map(cl => <option key={cl.ClassName} value={cl.ClassName}>{cl.ClassName}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Fee Type</label>
                  <select
                    value={classPricingForm.feeTypeId}
                    onChange={e => setClassPricingForm(f => ({ ...f, feeTypeId: e.target.value }))}
                    className="border rounded-lg px-2 py-1.5 text-sm"
                  >
                    <option value="">— fee —</option>
                    {feeTypes.map(ft => (
                      <option key={ft.FeeTypeID} value={ft.FeeTypeID}>{ft.FeeTypeName} ({ft.Currency})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Students</label>
                  <select
                    value={classPricingForm.studentType}
                    onChange={e => setClassPricingForm(f => ({ ...f, studentType: e.target.value }))}
                    className="border rounded-lg px-2 py-1.5 text-sm"
                  >
                    <option value="All">All</option>
                    <option value="New">New only</option>
                    <option value="Returning">Returning only</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Amount</label>
                  <input
                    type="number"
                    value={classPricingForm.amount}
                    onChange={e => setClassPricingForm(f => ({ ...f, amount: e.target.value }))}
                    className="w-32 border rounded-lg px-2 py-1.5 text-sm text-right"
                  />
                </div>
                <Gate permission="registration.configure_pricing.configure" label="Add Class Pricing">
                  <button
                    onClick={handleAddClassPricing}
                    className="px-4 py-1.5 bg-amber-600 text-white text-sm rounded-lg hover:bg-amber-700 flex items-center gap-1"
                  >
                    <Plus size={14} /> Add
                  </button>
                </Gate>
              </div>

              {pricingData.classPricing.length === 0 ? (
                <p className="text-sm text-gray-500 italic">No per-class pricing yet — tuition amounts per class go here.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left py-2 px-2">Class</th>
                      <th className="text-left py-2 px-2">Fee</th>
                      <th className="text-left py-2 px-2">Students</th>
                      <th className="text-right py-2 px-2">Amount</th>
                      <th className="py-2 px-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pricingData.classPricing.map((row, idx) => (
                      <tr key={row.PricingID} className="border-b hover:bg-gray-50">
                        <td className="py-2 px-2 font-medium">{row.ClassName}</td>
                        <td className="py-2 px-2">{row.FeeTypeName} <span className="text-xs text-gray-400">({row.Currency})</span></td>
                        <td className="py-2 px-2">
                          <select
                            value={row.StudentType || 'All'}
                            onChange={e => setClassPricingField(idx, 'StudentType', e.target.value)}
                            className="border rounded px-1 py-0.5 text-sm"
                          >
                            <option value="All">All</option>
                            <option value="New">New</option>
                            <option value="Returning">Returning</option>
                          </select>
                        </td>
                        <td className="py-2 px-2 text-right">
                          <input
                            type="number"
                            value={row.Amount ?? ''}
                            onChange={e => setClassPricingField(idx, 'Amount', e.target.value)}
                            className="w-32 border rounded px-2 py-0.5 text-sm text-right"
                          />
                          <span className="ml-1 text-xs text-gray-400">{fmtAmt(row.Amount, row.Currency).replace(/[\d,.]+ ?/, '')}</span>
                        </td>
                        <td className="py-2 px-2 text-right whitespace-nowrap">
                          <Gate permission="registration.configure_pricing.edit" label="Edit Class Pricing">
                            <button onClick={() => handleSaveClassPricing(row)} className="text-blue-600 text-xs hover:underline mr-3">Save</button>
                          </Gate>
                          <Gate permission="registration.configure_pricing.configure" label="Delete Class Pricing">
                            <button onClick={() => handleDeleteClassPricing(row)} className="text-red-600 text-xs hover:underline">Delete</button>
                          </Gate>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ============================================
  // RENDER: MODALS
  // ============================================
  const renderModals = () => (
    <>
      {/* New Registration Modal */}
      {showNewRegistration && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">New Family Registration</h3>
              <button onClick={() => setShowNewRegistration(false)}><X size={20} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Father's Name *</label>
                <input
                  type="text"
                  value={newFamilyForm.fatherName}
                  onChange={(e) => setNewFamilyForm({...newFamilyForm, fatherName: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Mother's Name</label>
                <input
                  type="text"
                  value={newFamilyForm.motherName}
                  onChange={(e) => setNewFamilyForm({...newFamilyForm, motherName: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              {dupMatches.length > 0 && (
                <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg">
                  <p className="text-xs font-semibold text-amber-800 mb-2">
                    ⚠ Similar families already exist — don't create a duplicate. For a school
                    family use "Re-Register Family"; for any existing family (incl. daycare-only)
                    click "Use this family" to register a new student under it:
                  </p>
                  <div className="space-y-1 max-h-40 overflow-auto">
                    {dupMatches.map(m => (
                      <div key={m.FamilyID} className="flex justify-between items-center gap-2 text-xs bg-white rounded px-2 py-1.5 border border-amber-100">
                        <span className="min-w-0">
                          <span className="font-medium">{m.FatherName}</span>
                          {m.MotherName && <span className="text-gray-500"> & {m.MotherName}</span>}
                          <span className="text-gray-400"> · {m.FamilyID}</span>
                        </span>
                        <span className="text-gray-500 whitespace-nowrap pl-2">
                          {m.ActiveChildren} active child{m.ActiveChildren === 1 ? '' : 'ren'}
                          {m.PrimaryPhone && ` · ${m.PrimaryPhone}`}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleUseExistingFamily(m)}
                          disabled={loading}
                          className="ml-auto whitespace-nowrap px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                        >
                          Use this family
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-1">Phone *</label>
                <input
                  type="tel"
                  value={newFamilyForm.primaryPhone}
                  onChange={(e) => setNewFamilyForm({...newFamilyForm, primaryPhone: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email</label>
                <input
                  type="email"
                  value={newFamilyForm.email}
                  onChange={(e) => setNewFamilyForm({...newFamilyForm, email: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Address</label>
                <input
                  type="text"
                  value={newFamilyForm.address}
                  onChange={(e) => setNewFamilyForm({...newFamilyForm, address: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <button
                onClick={handleCreateNewRegistration}
                disabled={!newFamilyForm.fatherName || !newFamilyForm.primaryPhone || loading}
                className="w-full py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create Registration'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Re-Registration Modal (step 1: pick family, step 2: pick children + classes) */}
      {showReRegistration && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[80vh] overflow-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {reRegFamily
                  ? `Re-Register: ${reRegFamily.FatherName} (${reRegFamily.FamilyID})`
                  : 'Re-Register Existing Family'}
              </h3>
              <button onClick={() => { setShowReRegistration(false); setReRegFamily(null); setReRegChildren([]); setReRegSearch(''); }}>
                <X size={20} />
              </button>
            </div>

            {!reRegFamily ? (
              <>
                <p className="text-sm text-gray-600 mb-4">
                  Select a family to create their {registrationYear} re-registration.
                  {' '}{eligibleFamilies.length} families eligible.
                </p>
                <Gate permission="registration.bulk_re_registration.process" label="Bulk Re-Register All">
                  <button
                    onClick={handleBulkReRegistration}
                    className="mb-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                  >
                    Bulk Create All ({eligibleFamilies.length})
                  </button>
                </Gate>
                <div className="relative mb-3">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={reRegSearch}
                    onChange={e => setReRegSearch(e.target.value)}
                    placeholder="Search by father name, family ID, or child..."
                    className="w-full border rounded-lg pl-9 pr-3 py-2 text-sm"
                    autoFocus
                  />
                </div>
                <div className="space-y-2 max-h-96 overflow-auto">
                  {eligibleFamilies.filter(f => {
                    const q = reRegSearch.trim().toLowerCase();
                    if (!q) return true;
                    return (f.FatherName || '').toLowerCase().includes(q)
                      || (f.MotherName || '').toLowerCase().includes(q)
                      || (f.FamilyID || '').toLowerCase().includes(q)
                      || (f.ChildrenList || '').toLowerCase().includes(q);
                  }).map(family => (
                    <div key={family.FamilyID} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium">{family.FatherName}</p>
                        <p className="text-sm text-gray-500">{family.FamilyID} • {family.EligibleChildren} children</p>
                        <p className="text-xs text-gray-400">{family.ChildrenList}</p>
                      </div>
                      <button
                        onClick={() => handleOpenReRegPreview(family)}
                        disabled={loading}
                        className="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
                      >
                        Select Children
                      </button>
                    </div>
                  ))}
                  {eligibleFamilies.length === 0 && (
                    <p className="text-center text-gray-500 py-4">All families already registered or no eligible families found.</p>
                  )}
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-600 mb-4">
                  Choose who to include in the {registrationYear} registration and the class each child applies for.
                  Untick children who left or graduated; keep the same class for repeaters.
                </p>
                <div className="space-y-2 max-h-96 overflow-auto mb-4">
                  {reRegChildren.map((child, idx) => (
                    <div
                      key={child.StudentID}
                      className={`flex items-center gap-3 p-3 rounded-lg border ${child.include ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}
                    >
                      <input
                        type="checkbox"
                        checked={child.include}
                        onChange={e => setReRegChildren(rows =>
                          rows.map((r, i) => i === idx ? { ...r, include: e.target.checked } : r))}
                        className="w-4 h-4"
                      />
                      <div className="flex-1 min-w-0">
                        <p className={`font-medium ${child.include ? 'text-gray-800' : 'text-gray-400 line-through'}`}>
                          {child.FirstName} {child.LastName}
                        </p>
                        <p className="text-xs text-gray-500">
                          Current: {child.CurrentClass || '—'}
                          {!!child.IsGraduating && <span className="ml-2 text-amber-600 font-semibold">Graduating</span>}
                          {child.SectionOptions && (
                            <span className="ml-2 text-blue-600 font-semibold">
                              Choose section: {child.SectionOptions.join(' / ')}
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">Applying for</span>
                        <select
                          value={child.applyingForClass}
                          disabled={!child.include}
                          onChange={e => setReRegChildren(rows =>
                            rows.map((r, i) => i === idx ? { ...r, applyingForClass: e.target.value } : r))}
                          className="border rounded-lg px-2 py-1 text-sm disabled:bg-gray-100 disabled:text-gray-400"
                        >
                          <option value="">— class —</option>
                          {classLevels.map(cl => (
                            <option key={cl.ClassName} value={cl.ClassName}>{cl.ClassName}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ))}
                  {reRegChildren.length === 0 && (
                    <p className="text-center text-gray-500 py-4">No active children found for this family.</p>
                  )}
                </div>
                <div className="flex justify-between items-center">
                  <button
                    onClick={() => { setReRegFamily(null); setReRegChildren([]); }}
                    className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                  >
                    ← Back to families
                  </button>
                  <button
                    onClick={handleCreateReRegistration}
                    disabled={loading || reRegChildren.every(c => !c.include)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    {loading ? 'Creating...' : `Create Registration (${reRegChildren.filter(c => c.include).length} children)`}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modular Family Report — see modals/FamilyReportModal.
          mode 'family' (this family, printable) or 'all' (table + export). */}
      {reportMode && (
        <FamilyReportModal
          apiCall={apiCall}
          mode={reportMode}
          familyId={familyDetails?.FamilyID || selectedRegistration?.FamilyID}
          familyDetails={familyDetails}
          academicYear={registrationYear}
          showError={showError}
          onClose={() => setReportMode(null)}
        />
      )}

      {/* Payment Pledge Print Modal (تعهد) — see modals/RegistrationPledgePrintModal */}
      {showPrintPlan && registrationDetails && (
        <RegistrationPledgePrintModal
          apiCall={apiCall}
          registration={registrationDetails.registration}
          regChildren={registrationDetails.children}
          paymentPlan={registrationDetails.paymentPlan}
          pledgeDiscounts={registrationDetails.pledgeDiscounts}
          registrationYear={registrationYear}
          allowEdit={pledgeAllowEdit}
          showSuccess={showSuccess}
          showError={showError}
          onClose={() => setShowPrintPlan(false)}
        />
      )}

      {/* Lookup Lists Manager (GlobalAdmin) — see modals/RegistrationLookupsModal */}
      {showLookupsManager && (
        <RegistrationLookupsModal
          apiCall={apiCall}
          showSuccess={showSuccess}
          showError={showError}
          onClose={() => setShowLookupsManager(false)}
          onChanged={loadLookups}
        />
      )}

      {/* Family Details Edit Modal */}
      {showFamilyEdit && familyDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-5xl max-h-[88vh] overflow-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Edit Family Details — {familyDetails.FamilyID}</h3>
              <button onClick={() => setShowFamilyEdit(false)}><X size={20} /></button>
            </div>

            {/* Parents side by side, each grouped (name → Arabic → nationality →
                flags → boxed Work) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {renderParentCard('father', 'Father — الأب')}
              {renderParentCard('mother', 'Mother — الأم')}
            </div>

            {/* Household */}
            <div className="border rounded-lg p-3 mt-4 bg-gray-50/40">
              <div className="text-sm font-semibold text-gray-700 mb-2">Household</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div>
                  <label className="block text-[11px] text-gray-500 mb-0.5">Family Status</label>
                  <select value={familyForm.familyStatus} onChange={e => setFamilyForm(f => ({ ...f, familyStatus: e.target.value }))}
                    className="w-full border rounded-lg px-2 py-1.5 text-sm">
                    <option value="">— not set —</option>
                    {(lookups.FamilyStatus || []).map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] text-gray-500 mb-0.5">Religion</label>
                  <select value={familyForm.religion} onChange={e => setFamilyForm(f => ({ ...f, religion: e.target.value }))}
                    className="w-full border rounded-lg px-2 py-1.5 text-sm">
                    <option value="">— not set —</option>
                    {(lookups.Religion || []).map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] text-gray-500 mb-0.5">Marital Status</label>
                  <select value={familyForm.maritalStatus} onChange={e => setFamilyForm(f => ({ ...f, maritalStatus: e.target.value }))}
                    className="w-full border rounded-lg px-2 py-1.5 text-sm">
                    <option value="">— not set —</option>
                    {(lookups.MaritalStatus || []).map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-[11px] text-gray-500 mb-0.5">Email</label>
                  <input type="email" value={familyForm.email} onChange={e => setFamilyForm(f => ({ ...f, email: e.target.value }))}
                    className="w-full border rounded-lg px-2 py-1.5 text-sm" />
                </div>
                <div className="md:col-span-4">
                  <label className="block text-[11px] text-gray-500 mb-0.5">Home Address</label>
                  <input type="text" value={familyForm.address} onChange={e => setFamilyForm(f => ({ ...f, address: e.target.value }))}
                    className="w-full border rounded-lg px-2 py-1.5 text-sm" />
                </div>
              </div>
            </div>

            {/* Lebanese registry: cascading محافظة → قضاء → محلة + رقم السجل */}
            <div className="border rounded-lg p-3 mt-4 bg-gray-50/40">
              <div className="text-sm font-semibold text-gray-700 mb-2">Lebanese Registry — القيد</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div>
                  <label className="block text-[11px] text-gray-500 mb-0.5">Governorate — المحافظة</label>
                  <select value={geoSel.governorateId} onChange={e => onGeoGovernorate(e.target.value)}
                    className="w-full border rounded-lg px-2 py-1.5 text-sm">
                    <option value="">— select —</option>
                    {geo.governorates.map(g => <option key={g.GovernorateID} value={g.GovernorateID}>{g.NameAr}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] text-gray-500 mb-0.5">Caza — القضاء</label>
                  <select value={geoSel.cazaId} onChange={e => onGeoCaza(e.target.value)} disabled={!geoSel.governorateId}
                    className="w-full border rounded-lg px-2 py-1.5 text-sm disabled:bg-gray-100">
                    <option value="">— select —</option>
                    {geo.cazas.map(c => <option key={c.CazaID} value={c.CazaID}>{c.NameAr}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] text-gray-500 mb-0.5">Locality/Village — المحلة/القرية</label>
                  <select value={familyForm.registrationPlaceId || ''} onChange={e => setFamilyForm(f => ({ ...f, registrationPlaceId: e.target.value }))}
                    disabled={!geoSel.cazaId} className="w-full border rounded-lg px-2 py-1.5 text-sm disabled:bg-gray-100">
                    <option value="">— select —</option>
                    {geo.localities.map(l => <option key={l.LocalityID} value={l.LocalityID}>{l.NameAr}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] text-gray-500 mb-0.5">Register No. — رقم السجل</label>
                  <input type="text" value={familyForm.registerNumber || ''} onChange={e => setFamilyForm(f => ({ ...f, registerNumber: e.target.value }))}
                    className="w-full border rounded-lg px-2 py-1.5 text-sm" />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowFamilyEdit(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleSaveFamilyDetails} disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {loading ? 'Saving...' : 'Save Details'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Child Modal */}
      {changeClassChild && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Change Class</h3>
              <button onClick={() => setChangeClassChild(null)}><X size={20} /></button>
            </div>
            <div className="space-y-4">
              <p className="text-sm text-gray-700">
                <span className="font-medium">{changeClassChild.FullName}</span> — currently{' '}
                <span className="font-medium">{changeClassChild.ApplyingForClass}</span>
              </p>
              <div>
                <label className="block text-sm font-medium mb-1">New Class *</label>
                <select
                  value={changeClassValue}
                  onChange={(e) => setChangeClassValue(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">Select class...</option>
                  {classLevels.map(cl => (
                    <option key={cl.ClassID} value={cl.ClassName}>
                      {cl.ClassName} - {cl.ClassNameFrench}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Fees are re-priced for the new class{changeClassChild.StudentID ? ', and the academic record is updated (section cleared — reassign it in Academic).' : '.'}{' '}
                  Blocked if fees have already been paid.
                </p>
              </div>
              <button
                onClick={handleChangeClass}
                disabled={loading || !changeClassValue || changeClassValue === changeClassChild.ApplyingForClass}
                className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Changing...' : 'Change Class'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddChild && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Add Child</h3>
              <button onClick={() => setShowAddChild(false)}><X size={20} /></button>
            </div>
            <div className="space-y-4">
              {familyStudents.length > 0 && (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setAddChildMode('existing')}
                    className={`flex-1 py-2 text-sm rounded-lg border ${addChildMode === 'existing' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300'}`}
                  >
                    Existing student
                  </button>
                  <button
                    type="button"
                    onClick={() => setAddChildMode('new')}
                    className={`flex-1 py-2 text-sm rounded-lg border ${addChildMode === 'new' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300'}`}
                  >
                    New student
                  </button>
                </div>
              )}

              {addChildMode === 'existing' ? (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-1">Student *</label>
                    <select
                      value={existingChildForm.studentId}
                      onChange={(e) => setExistingChildForm({...existingChildForm, studentId: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="">Select a child in this family…</option>
                      {familyStudents.map(s => (
                        <option key={s.StudentID} value={s.StudentID}>
                          {(s.FullName || `${s.FirstName || ''} ${s.LastName || ''}`.trim())} ({s.StudentID})
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      Existing students in this family not yet on this registration — the real student is used, no duplicate is created.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Applying for Class *</label>
                    <select
                      value={existingChildForm.applyingForClass}
                      onChange={(e) => setExistingChildForm({...existingChildForm, applyingForClass: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="">Select class...</option>
                      {classLevels.map(cl => (
                        <option key={cl.ClassID} value={cl.ClassName}>
                          {cl.ClassName} - {cl.ClassNameFrench}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">First Name *</label>
                      <input
                        type="text"
                        value={newChildForm.firstName}
                        onChange={(e) => setNewChildForm({...newChildForm, firstName: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Last Name *</label>
                      <input
                        type="text"
                        value={newChildForm.lastName}
                        onChange={(e) => setNewChildForm({...newChildForm, lastName: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Date of Birth</label>
                      <input
                        type="date"
                        value={newChildForm.dateOfBirth}
                        onChange={(e) => setNewChildForm({...newChildForm, dateOfBirth: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Gender</label>
                      <select
                        value={newChildForm.gender}
                        onChange={(e) => setNewChildForm({...newChildForm, gender: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                      >
                        <option value="">Select...</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Applying for Class *</label>
                    <select
                      value={newChildForm.applyingForClass}
                      onChange={(e) => setNewChildForm({...newChildForm, applyingForClass: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="">Select class...</option>
                      {classLevels.map(cl => (
                        <option key={cl.ClassID} value={cl.ClassName}>
                          {cl.ClassName} - {cl.ClassNameFrench}
                        </option>
                      ))}
                    </select>
                  </div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={newChildForm.isNewStudent}
                      onChange={(e) => setNewChildForm({...newChildForm, isNewStudent: e.target.checked})}
                      className="rounded"
                    />
                    <span className="text-sm">New Student (first time at school)</span>
                  </label>
                </>
              )}

              <button
                onClick={handleAddChild}
                disabled={loading || (addChildMode === 'existing'
                  ? (!existingChildForm.studentId || !existingChildForm.applyingForClass)
                  : (!newChildForm.firstName || !newChildForm.lastName || !newChildForm.applyingForClass))}
                className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Adding...' : 'Add Child'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Discount Modal */}
      {showDiscountModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Apply Family Discount</h3>
              <button onClick={() => setShowDiscountModal(false)}><X size={20} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Discount USD</label>
                <input
                  type="number"
                  value={discountForm.discountUSD}
                  onChange={(e) => setDiscountForm({...discountForm, discountUSD: parseFloat(e.target.value) || 0})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Discount LBP</label>
                <input
                  type="number"
                  value={discountForm.discountLBP}
                  onChange={(e) => setDiscountForm({...discountForm, discountLBP: parseFloat(e.target.value) || 0})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Reason *</label>
                <input
                  type="text"
                  value={discountForm.discountReason}
                  onChange={(e) => setDiscountForm({...discountForm, discountReason: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="e.g., Staff discount, Sibling discount"
                />
              </div>
              <button
                onClick={handleApplyDiscount}
                disabled={!discountForm.discountReason || loading}
                className="w-full py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                {loading ? 'Applying...' : 'Apply Discount'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Installment (month) Modal */}
      {showAddInstallment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Add Installment</h3>
              <button onClick={() => setShowAddInstallment(false)}><X size={20} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name * <span className="font-normal text-gray-400">(e.g. "October 2026")</span></label>
                <input
                  type="text"
                  value={addInstallmentForm.installmentName}
                  onChange={(e) => setAddInstallmentForm({...addInstallmentForm, installmentName: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Due Date *</label>
                <input
                  type="date"
                  value={addInstallmentForm.dueDate}
                  onChange={(e) => setAddInstallmentForm({...addInstallmentForm, dueDate: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Amount LBP</label>
                  <input
                    type="number"
                    value={addInstallmentForm.amountLBP}
                    onChange={(e) => setAddInstallmentForm({...addInstallmentForm, amountLBP: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Amount USD</label>
                  <input
                    type="number"
                    value={addInstallmentForm.amountUSD}
                    onChange={(e) => setAddInstallmentForm({...addInstallmentForm, amountUSD: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 -mt-2">
                Leave both amounts at 0 to <b>re-split the plan evenly</b> across all unpaid
                months including this one. Enter an amount to add it as a custom month on top.
              </p>
              <div>
                <label className="block text-sm font-medium mb-1">Notes</label>
                <input
                  type="text"
                  value={addInstallmentForm.notes}
                  onChange={(e) => setAddInstallmentForm({...addInstallmentForm, notes: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <button
                onClick={handleAddInstallment}
                disabled={loading}
                className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Adding...' : 'Add Installment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Installment Modal */}
      {showEditInstallment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Edit Installment</h3>
              <button onClick={() => setShowEditInstallment(false)}><X size={20} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name</label>
                <input
                  type="text"
                  value={installmentForm.InstallmentName || ''}
                  onChange={(e) => setInstallmentForm({...installmentForm, InstallmentName: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Due Date</label>
                <input
                  type="date"
                  value={installmentForm.DueDate?.split('T')[0] || ''}
                  onChange={(e) => setInstallmentForm({...installmentForm, DueDate: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Amount LBP</label>
                  <input
                    type="number"
                    value={installmentForm.AmountLBP || 0}
                    onChange={(e) => setInstallmentForm({...installmentForm, AmountLBP: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Amount USD</label>
                  <input
                    type="number"
                    value={installmentForm.AmountUSD || 0}
                    onChange={(e) => setInstallmentForm({...installmentForm, AmountUSD: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>
              <button
                onClick={handleUpdateInstallment}
                className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  // ============================================
  // MAIN RENDER
  // ============================================
  return (
    <div>
      {/* Header with Year */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Registration {registrationYear}</h1>
          <p className="text-gray-600">Manage student registrations for the upcoming academic year</p>
        </div>
        <button
          onClick={() => { loadRegistrations(); loadStats(); }}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Top-level tabs (hidden while viewing a single family's detail) */}
      {activeTab !== 'detail' && (
        <div className="mb-5 border-b border-gray-200 flex gap-1">
          <button
            onClick={() => { setActiveTab('list'); setRegistrationDetails(null); }}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${activeTab === 'reports' ? 'border-transparent text-gray-500 hover:text-gray-700' : 'border-blue-600 text-blue-600'}`}
          >
            Registrations
          </button>
          <Gate permission="registration.family_reports.view" label="Reports tab">
            <button
              onClick={() => setActiveTab('reports')}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${activeTab === 'reports' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              Reports
            </button>
          </Gate>
        </div>
      )}

      {/* Views */}
      {activeTab === 'list' && renderList()}
      {activeTab === 'detail' && renderDetail()}
      {activeTab === 'overdue' && renderOverdue()}
      {activeTab === 'pricing' && renderPricing()}
      {activeTab === 'reports' && (
        <RegistrationReports apiCall={apiCall} academicYear={registrationYear} showError={showError} />
      )}

      {/* Modals */}
      {renderModals()}
    </div>
  );
};

export default Registration;
